import { OfficeBackButton } from '../shared/OfficeBackButton';
import { useBaseStandardUi } from '../../lib/hooks/useBaseStandardUi';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, Info, DollarSign, Ban as Bandage, Clock, RefreshCw, Eye } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { MyTeamsTable } from './MyTeamsTable';
import { PositionFilterMyTeams } from './PositionFilterMyTeams';
import { GoaliesTable } from './GoaliesTable';
import { Position, SortConfig, Goalie, PoolContribution } from './typesMyTeams';
import { supabase } from '../../lib/supabase';
import { TeamLogo } from '../TeamLogo';
import { 
  releasePlayer, 
  togglePlayerReserveStatus, 
  swapPlayers, 
  swapPlayerWithScratch, 
  swapScratchWithReserve, 
  swapFourPlayers,
  getMyteamPlayer, 
  getMyTeamGoalies, 
  getDemotionInfo, 
  getMaxSigningInfo, 
  movePlayerToScratch, 
  removePlayerFromScratch, 
  promotePlayerFromReserves, 
  updatePlayerTradingStatus, 
  updatePlayerIntouchableStatus,
  getUserDraftPicks,
  addToWatchlist,
  removeFromWatchlist,
  getWatchlistPlayerIds,
  reactivateInjuredWithSwap,
  reactivateInjuredPlayerDirect,
  reactivateInjuredReplacingPlayer,
  getTeamRetentionDebt,
  getStandardPlayerSelectionStats
} from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';
import { isOnboardingDisabled } from '../../lib/onboarding';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import toast from 'react-hot-toast';
import { translate } from '../../lib/translations';
import { SwapPlayerModal } from './SwapPlayerModal';
import { SwapFourPlayersModal } from './SwapFourPlayersModal';
import { PromotePlayerModal } from './PromotePlayerModal';
import { DemoteConfirmModal } from './DemoteConfirmModal';
import { ReactivateInjuredPlayerModal } from './ReactivateInjuredPlayerModal';
import { MyTeamsModal, MyTeamsModalProvider } from './MyTeamsModal';
import { MobilePlayerActions } from './MobilePlayerActions';
import { ActionLegend } from './ActionLegend';
import { DATA_REFRESH_EVENT, reloadAfterMutation } from '../../lib/embedSafeReload';
import { usePaginationStore } from '../../lib/paginationStore';
import { removeFromProtectionList } from '../../lib/api/protectionList';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import { resolveErrorMessage } from '../../lib/utils/errorMessage';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { useEmbedStore } from '../../lib/embedStore';
import { isTslhTenant } from '../../lib/tenantStore';
import { isReleaseBlockedByMovementLimit } from '../../lib/releaseMovementGuard';
import './MyTeamsEmbed.css';

// Arsenal embed fonts (same as MyOfficeEmbed)
const ARSENAL_FONTS_ID = 'dp-arsenal-embed-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  React.useEffect(() => {
    if (!enabled) return;
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

type TeamSection = 'MAIN' | 'RESERVES' | 'INJURED' | 'SCRATCH';
type ActiveTab = 'players' | 'goalies' | 'scratches' | 'scratchGoalies' | 'overview';

interface InjuryInfo {
  playerid: number;
  description: string;
  returned_date?: string;
  created_at?: string;
  days_remaining?: number;
  injury_unique_id?: string;
  returned_injury_unique_id?: string;
}

export function MyTeams() {
  // Tracker cette page
  usePageTracking('myteam');

  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const anchoredModals = isEmbed && /iPhone/.test(navigator.userAgent);
  useArsenalFonts(isEmbed);

  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const username = useUserStore((state) => state.username);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();
  const { isBuildPhase, isStandardFlexible } = useLeagueMode();
  const isBaseStandardUi = useBaseStandardUi(isStandardFlexible);
  const hideDemotionCard = isEmbed && selectedLeague?.id === 138;
  const [selectedPosition, setSelectedPosition] = useState<Position>('ALL');
  const [mainPlayers, setMainPlayers] = useState<any[]>([]);
  const [reservePlayers, setReservePlayers] = useState<any[]>([]);
  const [scratchPlayers, setScratchPlayers] = useState<any[]>([]);
  const [mainGoalies, setMainGoalies] = useState<any[]>([]);
  const [reserveGoalies, setReserveGoalies] = useState<any[]>([]);
  const [scratchGoalies, setScratchGoalies] = useState<any[]>([]);
  const [injuredPlayers, setInjuredPlayers] = useState<any[]>([]);
  const [injuredGoalies, setInjuredGoalies] = useState<any[]>([]);
  const [injuryDescriptions, setInjuryDescriptions] = useState<InjuryInfo[]>([]);
  const [draftPicks, setDraftPicks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const refreshAfterMutation = useCallback(() => {
    reloadAfterMutation();
  }, []);

  // Les modals enfants demandent le rafraîchissement via cet évènement plutôt que de
  // recharger le document (voir reloadAfterMutation).
  useEffect(() => {
    const handleRefresh = () => setReloadKey((k) => k + 1);
    window.addEventListener(DATA_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleRefresh);
  }, []);

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'points',
    direction: 'desc',
  });
  const [goalieSortConfig, setGoalieSortConfig] = useState<{
    field: keyof Goalie;
    direction: 'asc' | 'desc';
  }>({
    field: 'wins',
    direction: 'desc',
  });
  const [activeSection, setActiveSection] = useState<TeamSection>('MAIN');
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);
  const [selectedGoalie, setSelectedGoalie] = useState<number | null>(null);
  const [selectedPlayerSection, setSelectedPlayerSection] = useState<TeamSection | null>(null);
  const [selectedGoalieSection, setSelectedGoalieSection] = useState<TeamSection | null>(null);
  
  // État pour contrôler l'affichage de la section d'information de la ligue
  const [showLeagueInfo, setShowLeagueInfo] = useState(false);

  // Modal d'aide tutorielle (s'affiche en mode BPM/onboarding ou si forcé par l'env)
  const isBpmSession = useUserStore((s) => s.is_bpm_session);
  const isTslhSession = useUserStore((s) => s.is_tslh_session);
  const limitedTeamActions = isBpmSession || (isEmbed && !!isTslhSession);
  const FORCE_BPM_ONBOARDING = (import.meta.env as any).VITE_BPM_FORCE_ONBOARDING === 'true';
  const [showInjuryTutorialModal, setShowInjuryTutorialModal] = useState(false);

  useEffect(() => {
    // Respect global disable flag
    if (isOnboardingDisabled()) return;

    // Afficher la modale si nous sommes en mode BPM OU si l'option de
    // forçage via Vite est active. Si forcé, on ignore le flag de
    // 'déjà montré' pour faciliter les tests.
    try {
      if (!isBpmSession && !FORCE_BPM_ONBOARDING) return;
      const key = `bpm-injury-modal-shown-${currentUserId ?? 'anon'}`;
      const shown = localStorage.getItem(key) === '1';
      if (FORCE_BPM_ONBOARDING) {
        setShowInjuryTutorialModal(true);
      } else if (!shown) {
        setShowInjuryTutorialModal(true);
      }
    } catch (e) {
      // ignore
    }
  }, [isBpmSession, currentUserId, FORCE_BPM_ONBOARDING]);

  const closeInjuryTutorialModal = () => {
    try {
      const key = `bpm-injury-modal-shown-${currentUserId ?? 'anon'}`;
      // Si on est en mode forcé, ne pas persister la fermeture pour
      // permettre aux développeurs de voir la modale tant que l'env est vrai.
      if (!FORCE_BPM_ONBOARDING) {
        localStorage.setItem(key, '1');
      }
    } catch (e) {
      // ignore
    }
    setShowInjuryTutorialModal(false);
  };
  
  // Multi-selection state for 4-player swaps
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedMainPlayers, setSelectedMainPlayers] = useState<{ id: number; name: string; isGoalie: boolean }[]>([]);
  const [selectedReservePlayers, setSelectedReservePlayers] = useState<{ id: number; name: string; isGoalie: boolean }[]>([]);

  useEffect(() => {
    if (!limitedTeamActions) return;
    setMultiSelectMode(false);
    setSelectedMainPlayers([]);
    setSelectedReservePlayers([]);
  }, [limitedTeamActions]);
  
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showFourPlayerSwapModal, setShowFourPlayerSwapModal] = useState(false);
  const [swapDetails, setSwapDetails] = useState<{
    mainName: string;
    reserveName: string;
    mainId: number;
    reserveId: number;
    isGoalie: boolean;
    swapType?: 'normal' | 'scratch' | 'scratch-reserve';
  } | null>(null);
  const [processingSwap, setProcessingSwap] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promotePlayerInfo, setPromotePlayerInfo] = useState<{
    id: number;
    name: string;
    isGoalie: boolean;
  } | null>(null);
  const [processingPromotion, _setProcessingPromotion] = useState(false);
  
  // Watchlist states
  const [watchlistPlayerIds, setWatchlistPlayerIds] = useState<Set<number>>(new Set());
  const [watchlistActionInProgress, setWatchlistActionInProgress] = useState(false);
  const [showDemoteModal, setShowDemoteModal] = useState(false);
  const [demotePlayerInfo, setDemotePlayerInfo] = useState<{ id: number; name: string; isGoalie: boolean; raw: any } | null>(null);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivatePlayerInfo, setReactivatePlayerInfo] = useState<{ id: number; name: string; isGoalie: boolean; raw: any } | null>(null);
  const [pendingReleasePlayer, setPendingReleasePlayer] = useState<any | null>(null);
  const [isReleasingPlayer, setIsReleasingPlayer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [leagueSettings, setLeagueSettings] = useState<any>(null);
  const [nhlTeamAbbrev, setNhlTeamAbbrev] = useState<string | null>(null);
  const [currentSalaryCap, setCurrentSalaryCap] = useState<number | null>(null);
  const [currentReserveSalaryCap, setCurrentReserveSalaryCap] = useState<number | null>(null);
  const [demotionInfo, setDemotionInfo] = useState({ current: 0, max: 10 });
  const [signingInfo, setSigningInfo] = useState({ current: 0, max: 15 });
  const { itemsPerPage, setItemsPerPage } = usePaginationStore();
  const [processingAction, setProcessingAction] = useState(false);
  const [daysToComeback, setDaysToComeback] = useState<number>(14);
  const [retentionDebt, setRetentionDebt] = useState<number>(0);
  const [statsMode, setStatsMode] = useState<'nhl' | 'pool'>('nhl');
  const [poolStats, setPoolStats] = useState<Map<number, PoolContribution>>(new Map());
  const wasAnyModalOpenRef = useRef(false);
  const releaseConfirmModalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pendingReleasePlayer || anchoredModals) return;
    const timer = window.setTimeout(() => {
      if (releaseConfirmModalRef.current) {
        releaseConfirmModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [pendingReleasePlayer, anchoredModals]);

  useEffect(() => {
    const shouldScroll = showInjuryTutorialModal && !wasAnyModalOpenRef.current;
    wasAnyModalOpenRef.current = showInjuryTutorialModal;

    if (!shouldScroll || anchoredModals) return;

    const timer = window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);

    return () => window.clearTimeout(timer);
  }, [
    showInjuryTutorialModal,
    anchoredModals,
  ]);

  // Refs for mobile auto-scroll to swap help messages
  const playerSwapHelpRef = useRef<HTMLDivElement | null>(null);
  const goalieSwapHelpRef = useRef<HTMLDivElement | null>(null);

  // Ref for the page's main container so we can scroll/highlight it
  const pageRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const highlightElement = (el: HTMLElement) => {
    const originalBoxShadow = el.style.boxShadow;
    el.style.transition = 'box-shadow 0.3s, transform 0.3s';
    el.style.boxShadow = '0 0 0 6px rgba(245,166,35,0.55)';
    el.style.transform = 'translateY(-2px)';
    setTimeout(() => {
      el.style.boxShadow = originalBoxShadow;
      el.style.transform = 'translateY(0)';
    }, 2500);
  };

  useEffect(() => {
    if (anchoredModals) return;
    const maybeState = (location.state as any) ?? null;
    if (maybeState && typeof maybeState.bpmOnboardingTarget === 'number') {
      const idx = maybeState.bpmOnboardingTarget as number;
      // Tour card index 1 corresponds to "Mon équipe"
      if (idx === 1) {
        // Scroll the main page container into view and highlight it
        const target = pageRef.current ?? document.getElementById('myteams-page');
        if (target) {
          if (showInjuryTutorialModal) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          highlightElement(target as HTMLElement);
        }
      }
    }
  }, [location.key, showInjuryTutorialModal, anchoredModals]);
  
  // Refs for auto-scroll to 4-player swap completion buttons
  const fourPlayerSwapButtonOverviewRef = useRef<HTMLDivElement | null>(null);
  const fourPlayerSwapButtonPlayersRef = useRef<HTMLDivElement | null>(null);
  const fourPlayerSwapButtonGoaliesRef = useRef<HTMLDivElement | null>(null);

  const getErrorMessage = useCallback(
    (err: unknown, fallbackFr: string, fallbackEn: string) =>
      resolveErrorMessage(err, language, fallbackFr, fallbackEn),
    [language]
  );

  // Mobile-only: auto scroll to the explanatory message when a swap selection starts
  useEffect(() => {
    // Only run on small screens (Bootstrap md breakpoint < 768px)
    if (anchoredModals || typeof window === 'undefined' || window.innerWidth >= 768) return;

    // Don't scroll if modal is open
    if (showSwapModal) return;

    // Scroll for players tab
    if (
      activeTab === 'players' &&
      selectedPlayer &&
      selectedPlayerSection !== 'INJURED'
    ) {
      // Wait a tick for the alert to mount
      setTimeout(() => {
        playerSwapHelpRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }

    // Scroll for goalies tab
    if (
      activeTab === 'goalies' &&
      selectedGoalie &&
      selectedGoalieSection !== 'INJURED'
    ) {
      setTimeout(() => {
        goalieSwapHelpRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [activeTab, selectedPlayer, selectedPlayerSection, selectedGoalie, selectedGoalieSection, showSwapModal, anchoredModals]);

  // Auto-scroll to 4-player swap completion button when 4 players are selected
  useEffect(() => {
    if (anchoredModals) return;
    // Only scroll when exactly 4 players are selected and multi-select mode is active
    if (multiSelectMode && selectedMainPlayers.length === 2 && selectedReservePlayers.length === 2) {
      // Show celebration toast
      toast.success(
        language === 'fr' 
          ? '🎉 4 joueurs sélectionnés ! Prêt pour la permutation !' 
          : '🎉 4 players selected! Ready to swap!',
        {
          duration: 3000,
          position: 'top-center'
        }
      );
      
      // Wait a tick for the button to mount
      setTimeout(() => {
        let buttonRef = null;
        
        // Determine which button to scroll to based on active tab
        if (activeTab === 'overview') {
          buttonRef = fourPlayerSwapButtonOverviewRef;
        } else if (activeTab === 'players') {
          buttonRef = fourPlayerSwapButtonPlayersRef;
        } else if (activeTab === 'goalies') {
          buttonRef = fourPlayerSwapButtonGoaliesRef;
        }
        
        // Scroll to the button with smooth animation
        if (buttonRef?.current) {
          buttonRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  }, [multiSelectMode, selectedMainPlayers.length, selectedReservePlayers.length, activeTab, language, anchoredModals]);

  useEffect(() => {
    if (!selectedLeague) {
      navigate('/dashboard');
      return;
    }

    async function loadSettings() {
      try {
        const { data, error } = await getLeagueSettings(selectedLeague!.id!);
        if (error) throw error;
        setLeagueSettings(data);
        if (data && data.day_to_comeback) {
          setDaysToComeback(data.day_to_comeback);
        }
      } catch (error) {
        console.error('Error loading league settings:', error);
        toast.error('Failed to load league settings');
      }
    }

    loadSettings();
  }, [selectedLeague, navigate]);

  // Charger le logo NHL de l'équipe courante
  useEffect(() => {
    if (!selectedLeague || !currentUserId) return;

    async function loadTeamLogo() {
      const { data, error } = await supabase
        .from('leagues_users')
        .select('nhl_team_abbrev')
        .eq('league_id', selectedLeague!.id)
        .eq('user_id', Number(currentUserId))
        .single();

      if (!error && data) {
        setNhlTeamAbbrev((data as { nhl_team_abbrev?: string | null }).nhl_team_abbrev ?? null);
      }
    }

    loadTeamLogo();
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    if (!selectedLeague || !currentUserId) return;

    async function loadSalaryCaps() {
      try {
        // Fetch main club salary cap (without retention adjustments)
        const { data: mainCapData, error: mainCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', currentUserId)
          .eq('isnhl', true)
          .single();

        if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;
        
        let adjustedMainCap = mainCapData?.salary_cap || 0;

        // Adjust for salary retention: subtract received retention, add debt
        // Get players with retention received (salary reduction)
        const { data: playersData } = await supabase
          .from('getmyteamplayer')
          .select('salary, salary_retention_received_amount, isnhl')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', currentUserId);

        const { data: goaliesData } = await supabase
          .from('getmyteamgoalie')
          .select('salary, salary_retention_received_amount, isnhl')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', currentUserId);

        // Calculate total retention received for main club (salaries we DON'T pay)
        let totalRetentionReceived = 0;
        playersData?.forEach(p => {
          if (p.isnhl && p.salary_retention_received_amount) {
            totalRetentionReceived += p.salary_retention_received_amount;
          }
        });
        goaliesData?.forEach(g => {
          if (g.isnhl && g.salary_retention_received_amount) {
            totalRetentionReceived += g.salary_retention_received_amount;
          }
        });

        // Adjust: original cap - retention received (we pay less) + retention debt (we pay more)
        adjustedMainCap = adjustedMainCap - totalRetentionReceived + (retentionDebt || 0);
        
        setCurrentSalaryCap(adjustedMainCap);

        // Fetch reserve team salary cap
        const { data: reserveCapData, error: reserveCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', currentUserId)
          .eq('isnhl', false)
          .single();

        if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      } catch (error) {
        console.error('Error loading salary caps:', error);
      }
    }

    loadSalaryCaps();
  }, [selectedLeague, currentUserId, retentionDebt, reloadKey]);

  useEffect(() => {
    if (!selectedLeague || !currentUserId) return;

    async function fetchDemotionInfo() {
      const result = await getDemotionInfo(selectedLeague!.id, Number(currentUserId));
      if (result.success && result.data) {
        setDemotionInfo({
          current: Number((result.data as any).current) || 0,
          max: Number((result.data as any).max) || 0,
        });
      }
    }

    async function fetchSigningInfo() {
      const result = await getMaxSigningInfo(selectedLeague!.id, Number(currentUserId));
      if (result.success && result.data) {
        setSigningInfo({
          current: Number((result.data as any).current) || 0,
          max: Number((result.data as any).max) || 0,
        });
      }
    }

    fetchDemotionInfo();
    fetchSigningInfo();
  }, [selectedLeague, currentUserId, reloadKey]);

  useEffect(() => {
    if (!selectedLeague || !currentUserId) {
      navigate('/dashboard');
      return;
    }

    async function loadPlayerData() {
      try {
        setLoading(true);

        // Fetch players
        const { data: allPlayers, error: playerError } = await getMyteamPlayer(
          selectedLeague!.id,
          Number(currentUserId)
        );

        if (playerError) throw playerError;

        // Fetch goalies
        const { data: allGoalies, error: goalieError } = await getMyTeamGoalies(
          selectedLeague!.id,
          Number(currentUserId)
        );

        if (goalieError) throw goalieError;

        const selectionStatsResult = isStandardFlexible
          ? await getStandardPlayerSelectionStats(selectedLeague!.id)
          : { data: null };
        const selectionPercentageByPlayerId = new Map(
          (selectionStatsResult.data ?? []).map((stat) => [
            Number(stat.player_id),
            Number(stat.selection_percentage ?? 0),
          ])
        );
        const withSelectionPercentage = <T extends { playerid: number }>(players: T[]) =>
          players.map((player) => ({
            ...player,
            ...(isStandardFlexible
              ? { selection_percentage: selectionPercentageByPlayerId.get(Number(player.playerid)) ?? 0 }
              : {}),
          }));
        const playersWithSelectionPercentage = allPlayers ? withSelectionPercentage(allPlayers) : null;
        const goaliesWithSelectionPercentage = allGoalies ? withSelectionPercentage(allGoalies) : null;

        // Fetch injury data from injury_player table with created_at timestamp and injury_unique_id
        const { data: injuryData, error: injuryError } = await supabase
          .from('injury_player')
          .select('playerid, description, created_at, injury_unique_id');

        if (injuryError) throw injuryError;

        // Fetch return dates from injury_return table with injury_unique_id
        const { data: returnData, error: returnError } = await supabase
          .from('injury_return')
          .select('playerid, returned_date, injury_unique_id');

        if (returnError) throw returnError;

        // Combine injury data with return dates and calculate days remaining
        const combinedInjuryData: InjuryInfo[] = [];
        
        // Add entries from injury_player
        injuryData?.forEach(injury => {
          combinedInjuryData.push({
            playerid: injury.playerid,
            description: injury.description || 'Joueur blessé',
            created_at: injury.created_at,
            injury_unique_id: injury.injury_unique_id
          });
        });
        
        // Add or update with return dates - only if injury_unique_id matches
        returnData?.forEach(returnInfo => {
          const existingIndex = combinedInjuryData.findIndex(i => i.playerid === returnInfo.playerid);
          if (existingIndex >= 0) {
            // Store the returned_injury_unique_id to compare later
            combinedInjuryData[existingIndex].returned_injury_unique_id = returnInfo.injury_unique_id;
            
            // Only set returned_date if injury_unique_id matches (same injury)
            if (combinedInjuryData[existingIndex].injury_unique_id === returnInfo.injury_unique_id) {
              combinedInjuryData[existingIndex].returned_date = returnInfo.returned_date;
              
              // Calculate remaining days if both dates exist
              if (combinedInjuryData[existingIndex].created_at && returnInfo.returned_date) {
                const createdDate = new Date(combinedInjuryData[existingIndex].created_at!);
                const returnedDate = new Date(returnInfo.returned_date);
                const daysPassed = Math.floor((returnedDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
                const daysRemaining = daysToComeback - daysPassed;
                combinedInjuryData[existingIndex].days_remaining = Math.max(0, daysRemaining);
              }
            }
          } else {
            // Player has a return record but no current injury record
            combinedInjuryData.push({
              playerid: returnInfo.playerid,
              description: 'Joueur précédemment blessé',
              returned_date: returnInfo.returned_date,
              returned_injury_unique_id: returnInfo.injury_unique_id
            });
          }
        });

        setInjuryDescriptions(combinedInjuryData);

        // Fetch injured status from league_players_user
        const { data: injuredStatusData, error: injuredStatusError } = await supabase
          .from('league_players_user')
          .select('player_id, is_injured')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', currentUserId)
          .eq('is_injured', true);

        if (injuredStatusError) throw injuredStatusError;

        // Fetch scratch players status from league_players_user
        const { data: scratchStatusData, error: scratchStatusError } = await supabase
          .from('league_players_user')
          .select('player_id, is_scratch')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', currentUserId)
          .eq('is_scratch', true);

        if (scratchStatusError) throw scratchStatusError;

        // Create sets of player IDs for fast lookup
        const injuredPlayerIds = new Set(injuredStatusData?.map(p => p.player_id) || []);
        const scratchPlayerIds = new Set(scratchStatusData?.map(p => p.player_id) || []);

        // Split players and goalies by status
        if (playersWithSelectionPercentage) {
          const main = [];
          const reserve = [];
          const injured = [];
          const scratch = [];

          for (const player of playersWithSelectionPercentage) {
            if (injuredPlayerIds.has(player.playerid)) {
              injured.push(player);
            } else if (scratchPlayerIds.has(player.playerid)) {
              scratch.push(player);
            } else if (player.isnhl) {
              main.push(player);
            } else {
              reserve.push(player);
            }
          }

          setMainPlayers(main);
          setReservePlayers(reserve);
          setInjuredPlayers(injured);
          setScratchPlayers(scratch);
        }

        if (goaliesWithSelectionPercentage) {
          const main = [];
          const reserve = [];
          const injured = [];
          const scratch = [];

          for (const goalie of goaliesWithSelectionPercentage) {
            if (injuredPlayerIds.has(goalie.playerid)) {
              injured.push(goalie);
            } else if (goalie.is_scratch) {
              scratch.push(goalie);
            } else if (goalie.isnhl) {
              main.push(goalie);
            } else {
              reserve.push(goalie);
            }
          }

          setMainGoalies(main);
          setReserveGoalies(reserve);
          setInjuredGoalies(injured);
          setScratchGoalies(scratch);
        }

        // Fetch draft picks
        const { data: draftPicksData, error: draftPicksError } = await getUserDraftPicks(
          selectedLeague!.id,
          Number(currentUserId)
        );

        if (draftPicksError) {
          console.error('Error loading draft picks:', draftPicksError);
        } else {
          setDraftPicks(draftPicksData || []);
        }

        // Load watchlist player IDs
        const watchlistResult = await getWatchlistPlayerIds(selectedLeague!.id, Number(currentUserId));
        if (watchlistResult.data) {
          setWatchlistPlayerIds(new Set(watchlistResult.data));
        }

        // Load salary retention debt
        const retentionResult = await getTeamRetentionDebt(selectedLeague!.id, Number(currentUserId));
        setRetentionDebt(retentionResult.totalRetainedSalary || 0);

        // Load pool contributions (stats pool du GM pour cette ligue)
        const { data: poolData } = await supabase
          .from('player_pool_contributions')
          .select('player_id, is_goalie, pool_goals, pool_assists, pool_wins, pool_shutouts, pool_gamesplayed')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', Number(currentUserId));
        if (poolData) {
          const map = new Map<number, PoolContribution>();
          for (const row of poolData) {
            map.set(row.player_id, {
              ...row,
              pool_points: (row.pool_goals ?? 0) + (row.pool_assists ?? 0) + (row.pool_wins ?? 0) + (row.pool_shutouts ?? 0)
            } as PoolContribution);
          }
          setPoolStats(map);
        }
      } catch (error) {
        console.error('Error loading player data:', error);
        toast.error('Failed to load player data');
      } finally {
        setLoading(false);
      }
    }

    loadPlayerData();
  }, [selectedLeague, currentUserId, navigate, daysToComeback, reloadKey, isStandardFlexible]);

  const handleSort = (field: any) => {
    setSortConfig((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const handleGoalieSort = (field: keyof Goalie) => {
    setGoalieSortConfig((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleViewPlayerDetails = (player: { playerid: number }) => {
    navigate(`/players/${player.playerid}`);
  };

  // Watchlist handlers
  const handleAddToWatchlist = async (playerId: number) => {
    if (!currentUserId || !selectedLeague || watchlistActionInProgress) return;

    // Trouver les informations du joueur
    const allPlayerArrays = [...mainPlayers, ...reservePlayers, ...scratchPlayers, ...injuredPlayers];
    const allGoalieArrays = [...mainGoalies, ...reserveGoalies, ...scratchGoalies, ...injuredGoalies];
    const player = allPlayerArrays.find(p => p.playerid === playerId) || allGoalieArrays.find(g => g.playerid === playerId);
    
    if (!player) {
      toast.error('Joueur introuvable');
      return;
    }

    try {
      setWatchlistActionInProgress(true);
      const result = await addToWatchlist(
        selectedLeague.id, 
        Number(currentUserId), 
        playerId,
        player.playerfullname || player.goaliefullname || '',
        player.position || 'G',
        !!player.goaliefullname, // isGoalie
        player.salary,
        player.teamabbrevs || ''
      );
      
      if (result.success) {
        setWatchlistPlayerIds(prev => new Set([...prev, playerId]));
        toast.success(
          language === 'fr' 
            ? 'Joueur ajouté à la liste de surveillance' 
            : 'Player added to watchlist'
        );
      } else {
        throw new Error(result.error?.message || 'Erreur lors de l\'ajout à la liste de surveillance');
      }
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de l\'ajout à la liste de surveillance'
          : 'Error adding to watchlist'
      );
    } finally {
      setWatchlistActionInProgress(false);
    }
  };

  const handleRemoveFromWatchlist = async (playerId: number) => {
    if (!currentUserId || !selectedLeague || watchlistActionInProgress) return;

    try {
      setWatchlistActionInProgress(true);
      const result = await removeFromWatchlist(selectedLeague.id, Number(currentUserId), playerId);
      
      if (result.success) {
        setWatchlistPlayerIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(playerId);
          return newSet;
        });
        toast.success(
          language === 'fr' 
            ? 'Joueur retiré de la liste de surveillance' 
            : 'Player removed from watchlist'
        );
      } else {
        throw new Error(result.error?.message || 'Erreur lors de la suppression de la liste de surveillance');
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la suppression de la liste de surveillance'
          : 'Error removing from watchlist'
      );
    } finally {
      setWatchlistActionInProgress(false);
    }
  };

  const getReleaseBlockedMessage = (): string | null => {
    const blocked = isReleaseBlockedByMovementLimit({
      isEmbed,
      isBpmSession: !!isBpmSession,
      isTslhSession: !!isTslhSession,
      countFreeAgentSignings: leagueSettings?.count_free_agent_signings !== false,
      signingsUsed: signingInfo.current,
      signingsMax: signingInfo.max,
    });
    if (!blocked) return null;
    return translate('release.blocked.no.movement', language, {
      current: String(signingInfo.current),
      max: String(signingInfo.max),
    });
  };

  const handleRelease = (player: any) => {
    const blockedMessage = getReleaseBlockedMessage();
    if (blockedMessage) {
      toast.error(blockedMessage);
      return;
    }
    setPendingReleasePlayer(player);
  };

  const handleReleaseConfirmed = async () => {
    const player = pendingReleasePlayer;
    if (!player || !currentUserId || !selectedLeague) return;

    const blockedMessage = getReleaseBlockedMessage();
    if (blockedMessage) {
      setPendingReleasePlayer(null);
      toast.error(blockedMessage);
      return;
    }

    setIsReleasingPlayer(true);
    try {
      const { success, error } = await releasePlayer(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (!success) throw error;

      // Remove the player from the protection list
      await removeFromProtectionList(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      setPendingReleasePlayer(null);
      toast.success(`${player.skaterfullname || player.goaliefullname} released successfully`);

      // Attendre 1.5 seconde pour laisser le toast s'afficher avant de rafraîchir
      setTimeout(() => {
        refreshAfterMutation();
      }, 1500);
    } catch (error) {
      console.error('Error releasing player:', error);
      toast.error(getErrorMessage(error, 'Impossible de libérer ce joueur.', 'Failed to release player'));
    } finally {
      setIsReleasingPlayer(false);
    }
  };

  const handleToggleReserve = async (player: any) => {
    if (!currentUserId || !selectedLeague) return;

    console.log('=== DEBUG handleToggleReserve START ===');
    console.log('Player:', player);
    console.log('player.isnhl:', player.isnhl);

    // Promotion : ouvrir le modal de promotion
    if (!player.isnhl) {
      console.log('Player is in reserves, showing promotion modal...');
      setPromotePlayerInfo({
        id: player.playerid,
        name: player.skaterfullname || player.goaliefullname,
        isGoalie: !!player.goaliefullname
      });
      console.log('PromotePlayerInfo set to:', {
        id: player.playerid,
        name: player.skaterfullname || player.goaliefullname,
        isGoalie: !!player.goaliefullname
      });
      setShowPromoteModal(true);
      console.log('Modal should be shown now');
      return;
    }
    // Rétrogradation : ouvrir le modal de confirmation
    if (player.isnhl) {
      setDemotePlayerInfo({
        id: player.playerid,
        name: player.skaterfullname || player.goaliefullname,
        isGoalie: !!player.goaliefullname,
        raw: player
      });
      setShowDemoteModal(true);
      return;
    }
  };

  // Confirmation de la rétrogradation après modal
  const handleConfirmDemotion = async () => {
    if (!demotePlayerInfo || !currentUserId || !selectedLeague) return;
    const player = demotePlayerInfo.raw;
    setProcessingAction(true);
    try {
      if (player.isnhl && demotionInfo.current >= demotionInfo.max) {
        toast.error(translate('demotion.limit.reached', language, {
          current: demotionInfo.current.toString(),
          max: demotionInfo.max.toString()
        }));
        return;
      }

      const result = await togglePlayerReserveStatus(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id,
        player.isnhl
      );

      if (!result.success) {
        if (result.error?.message === 'Demotion limit reached') {
          toast.error(translate('demotion.limit.reached', language, {
            current: result.demotionLimit?.current?.toString() || demotionInfo.current.toString(),
            max: result.demotionLimit?.max?.toString() || demotionInfo.max.toString()
          }));
          setDemotionInfo({
            current: result.demotionLimit?.current || demotionInfo.current,
            max: result.demotionLimit?.max || demotionInfo.max
          });
          return;
        } else if (result.error?.message?.includes('Reserve salary cap would be exceeded')) {
          toast.error(language === 'fr'
            ? 'Impossible de rétrograder ce joueur. Le plafond salarial des réserves serait dépassé.'
            : 'Cannot demote this player. Reserve salary cap would be exceeded.');
          return;
        } else if (result.error?.message?.includes('Limite de joueurs de réserve atteinte') || result.error?.message?.includes('Limite de gardiens de réserve atteinte')) {
          toast.error(language === 'fr'
            ? 'Impossible de rétrograder ce joueur. Limite de joueurs de réserve atteinte.'
            : 'Cannot demote this player. Reserve team limit reached.');
          return;
        }
        throw result.error;
      }

      if (player.isnhl) {
        setDemotionInfo(prev => ({ ...prev, current: prev.current + 1 }));
      }

      toast.success(`${player.skaterfullname || player.goaliefullname} demoted to reserves`);
      setTimeout(() => { refreshAfterMutation(); }, 1500);
    } catch (error) {
      console.error('Error toggling player reserve status:', error);
      toast.error(getErrorMessage(error, 'Impossible de modifier le statut du joueur.', 'Failed to update player status'));
    } finally {
      setProcessingAction(false);
      setShowDemoteModal(false);
      setDemotePlayerInfo(null);
    }
  };

  // Toggle multi-select mode
  const toggleMultiSelectMode = () => {
    setMultiSelectMode(!multiSelectMode);
    // Clear all selections when toggling mode
    setSelectedMainPlayers([]);
    setSelectedReservePlayers([]);
    setSelectedPlayer(null);
    setSelectedGoalie(null);
    setSelectedPlayerSection(null);
    setSelectedGoalieSection(null);
  };

  // Handle multi-select for 4-player swap
  const handleMultiSelect = (player: any) => {
    const playerData = {
      id: player.playerid,
      name: player.skaterfullname || player.goaliefullname,
      isGoalie: !!player.goaliefullname
    };

    // Determine current section for this player
    const getPlayerSection = (playerId: number, isGoalie: boolean): TeamSection | null => {
      if (isGoalie) {
        if (mainGoalies.find(g => g.playerid === playerId)) return 'MAIN';
        if (reserveGoalies.find(g => g.playerid === playerId)) return 'RESERVES';
        if (scratchGoalies.find(g => g.playerid === playerId)) return 'SCRATCH';
        if (injuredGoalies.find(g => g.playerid === playerId)) return 'INJURED';
      } else {
        if (mainPlayers.find(p => p.playerid === playerId)) return 'MAIN';
        if (reservePlayers.find(p => p.playerid === playerId)) return 'RESERVES';
        if (scratchPlayers.find(p => p.playerid === playerId)) return 'SCRATCH';
        if (injuredPlayers.find(p => p.playerid === playerId)) return 'INJURED';
      }
      return null;
    };

    const section = getPlayerSection(player.playerid, !!player.goaliefullname);
    
    // Only allow selection from MAIN and RESERVES sections
    if (section !== 'MAIN' && section !== 'RESERVES') {
      toast.error(language === 'fr' 
        ? 'Seuls les joueurs du club principal et des réserves peuvent être sélectionnés pour un échange de 4 joueurs.' 
        : 'Only main club and reserve players can be selected for a 4-player swap.');
      return;
    }

    if (section === 'MAIN') {
      // Check if player is already selected
      const isSelected = selectedMainPlayers.some(p => p.id === player.playerid);
      
      if (isSelected) {
        // Remove from selection
        setSelectedMainPlayers(selectedMainPlayers.filter(p => p.id !== player.playerid));
      } else {
        // Add to selection if we haven't reached the limit
        if (selectedMainPlayers.length < 2) {
          setSelectedMainPlayers([...selectedMainPlayers, playerData]);
        } else {
          toast.error(language === 'fr' 
            ? 'Vous ne pouvez sélectionner que 2 joueurs du club principal.' 
            : 'You can only select 2 main club players.');
        }
      }
    } else if (section === 'RESERVES') {
      // Check if player is already selected
      const isSelected = selectedReservePlayers.some(p => p.id === player.playerid);
      
      if (isSelected) {
        // Remove from selection
        setSelectedReservePlayers(selectedReservePlayers.filter(p => p.id !== player.playerid));
      } else {
        // Add to selection if we haven't reached the limit
        if (selectedReservePlayers.length < 2) {
          setSelectedReservePlayers([...selectedReservePlayers, playerData]);
        } else {
          toast.error(language === 'fr' 
            ? 'Vous ne pouvez sélectionner que 2 joueurs des réserves.' 
            : 'You can only select 2 reserve players.');
        }
      }
    }

    // Check if we can open the 4-player swap modal
    if (selectedMainPlayers.length === 2 && selectedReservePlayers.length === 2) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        setShowFourPlayerSwapModal(true);
      }, 100);
    }
  };

  // Check if a player is selected in multi-select mode
  const isPlayerSelectedInMultiMode = (player: any) => {
    return selectedMainPlayers.some(p => p.id === player.playerid) || 
           selectedReservePlayers.some(p => p.id === player.playerid);
  };

  // Handle the 4-player swap confirmation
  const handleConfirmFourPlayerSwap = async () => {
    if (!selectedLeague || !currentUserId) return;
    
    const mainPlayersData = selectedMainPlayers.map(p => ({ id: p.id, isGoalie: p.isGoalie }));
    const reservePlayersData = selectedReservePlayers.map(p => ({ id: p.id, isGoalie: p.isGoalie }));
    
    try {
      const result = await swapFourPlayers(
        selectedLeague.id,
        Number(currentUserId),
        mainPlayersData,
        reservePlayersData
      );

      if (!result.success) {
        if (result.error.message?.includes('Limite de rétrogradations atteinte')) {
          toast.error(result.error.message);
        } else if (result.error.message?.includes('Plafond salarial')) {
          toast.error(result.error.message);
        } else if (result.error.message?.includes('Limite d\'attaquants') || 
                   result.error.message?.includes('Limite de défenseurs') || 
                   result.error.message?.includes('Limite de gardiens')) {
          toast.error(result.error.message);
        } else {
          throw new Error(result.error.message || result.error);
        }
        return { success: false };
      }

      const mainNames = selectedMainPlayers.map(p => p.name).join(', ');
      const reserveNames = selectedReservePlayers.map(p => p.name).join(', ');
      
      toast.success(language === 'fr' 
        ? `Permutation réussie : ${mainNames} ↔ ${reserveNames}` 
        : `Swap successful: ${mainNames} ↔ ${reserveNames}`);
      
      // Update demotion counter
      if (result.demotionInfo) {
        setDemotionInfo(result.demotionInfo);
      }

      return { success: true };
    } catch (error) {
      console.error('Error swapping four players:', error);
      toast.error(getErrorMessage(error, 'Impossible d\'effectuer la permutation de 4 joueurs.', 'Failed to swap 4 players'));
      return { success: false };
    } finally {
      setShowFourPlayerSwapModal(false);
      setSelectedMainPlayers([]);
      setSelectedReservePlayers([]);
      setMultiSelectMode(false);
    }
  };

  const handleSwapSelect = (player: any) => {
    if (player.goaliefullname) {
      // If the same goalie is already selected, unselect it
      if (selectedGoalie === player.playerid) {
        setSelectedGoalie(null);
        setSelectedGoalieSection(null);
        return;
      }
      
      // Otherwise select this goalie
      setSelectedGoalie(player.playerid);
      setSelectedGoalieSection(activeSection);
      setSelectedPlayer(null);
      setSelectedPlayerSection(null);
      
      // Check if we have a goalie from the other section selected
      const otherSectionGoalies = activeSection === 'MAIN' 
        ? [...reserveGoalies, ...scratchGoalies] 
        : activeSection === 'RESERVES'
        ? [...mainGoalies, ...scratchGoalies]
        : activeSection === 'SCRATCH'
        ? [...mainGoalies, ...reserveGoalies]
        : [];
        
      const selectedOtherGoalie = otherSectionGoalies.find(g => g.playerid === selectedGoalie);
      
      if (selectedOtherGoalie) {
        // Determine swap type based on sections
        let swapType: 'normal' | 'scratch' | 'scratch-reserve' = 'normal'; // normal swap between main and reserves
        let mainGoalie, otherGoalie;
        
        if (activeSection === 'MAIN' && selectedGoalieSection === 'RESERVES') {
          mainGoalie = player;
          otherGoalie = selectedOtherGoalie;
          swapType = 'normal';
        } else if (activeSection === 'RESERVES' && selectedGoalieSection === 'MAIN') {
          mainGoalie = selectedOtherGoalie;
          otherGoalie = player;
          swapType = 'normal';
        } else if (activeSection === 'MAIN' && selectedGoalieSection === 'SCRATCH') {
          mainGoalie = player;
          otherGoalie = selectedOtherGoalie;
          swapType = 'scratch';
        } else if (activeSection === 'SCRATCH' && selectedGoalieSection === 'MAIN') {
          mainGoalie = selectedOtherGoalie;
          otherGoalie = player;
          swapType = 'scratch';
        } else if (activeSection === 'SCRATCH' && selectedGoalieSection === 'RESERVES') {
          mainGoalie = player; // scratch goalie
          otherGoalie = selectedOtherGoalie; // reserve goalie
          swapType = 'scratch-reserve';
        } else if (activeSection === 'RESERVES' && selectedGoalieSection === 'SCRATCH') {
          mainGoalie = selectedOtherGoalie; // scratch goalie
          otherGoalie = player; // reserve goalie
          swapType = 'scratch-reserve';
        }
        
        setSwapDetails({
          mainName: mainGoalie.goaliefullname,
          reserveName: otherGoalie.goaliefullname,
          mainId: mainGoalie.playerid,
          reserveId: otherGoalie.playerid,
          isGoalie: true,
          swapType: swapType
        });
        
        setShowSwapModal(true);
      }
    } else {
      // If the same player is already selected, unselect it
      if (selectedPlayer === player.playerid) {
        setSelectedPlayer(null);
        setSelectedPlayerSection(null);
        return;
      }
      
      // Otherwise select this player
      setSelectedPlayer(player.playerid);
      setSelectedPlayerSection(activeSection);
      setSelectedGoalie(null);
      setSelectedGoalieSection(null);
      
      // Check if we have a player from the other section selected
      const otherSectionPlayers = activeSection === 'MAIN' 
        ? [...reservePlayers, ...scratchPlayers] 
        : activeSection === 'RESERVES'
        ? [...mainPlayers, ...scratchPlayers]
        : activeSection === 'SCRATCH'
        ? [...mainPlayers, ...reservePlayers]
        : [];
        
      const selectedOtherPlayer = otherSectionPlayers.find(p => p.playerid === selectedPlayer);
      
      if (selectedOtherPlayer) {
        // Determine swap type based on sections
        let swapType: 'normal' | 'scratch' | 'scratch-reserve' = 'normal'; // normal swap between main and reserves
        let mainPlayer, otherPlayer;
        
        if (activeSection === 'MAIN' && selectedPlayerSection === 'RESERVES') {
          mainPlayer = player;
          otherPlayer = selectedOtherPlayer;
          swapType = 'normal';
        } else if (activeSection === 'RESERVES' && selectedPlayerSection === 'MAIN') {
          mainPlayer = selectedOtherPlayer;
          otherPlayer = player;
          swapType = 'normal';
        } else if (activeSection === 'MAIN' && selectedPlayerSection === 'SCRATCH') {
          mainPlayer = player;
          otherPlayer = selectedOtherPlayer;
          swapType = 'scratch';
        } else if (activeSection === 'SCRATCH' && selectedPlayerSection === 'MAIN') {
          mainPlayer = selectedOtherPlayer;
          otherPlayer = player;
          swapType = 'scratch';
        } else if (activeSection === 'SCRATCH' && selectedPlayerSection === 'RESERVES') {
          mainPlayer = player; // scratch player
          otherPlayer = selectedOtherPlayer; // reserve player
          swapType = 'scratch-reserve';
        } else if (activeSection === 'RESERVES' && selectedPlayerSection === 'SCRATCH') {
          mainPlayer = selectedOtherPlayer; // scratch player
          otherPlayer = player; // reserve player
          swapType = 'scratch-reserve';
        }
        
        setSwapDetails({
          mainName: mainPlayer.skaterfullname,
          reserveName: otherPlayer.skaterfullname,
          mainId: mainPlayer.playerid,
          reserveId: otherPlayer.playerid,
          isGoalie: false,
          swapType: swapType
        });
        
        setShowSwapModal(true);
      }
    }
  };

  // Nouvelle fonction de swap pour la vue d'ensemble qui détermine automatiquement les sections
  const handleOverviewSwapSelect = (player: any) => {
    // Si en mode multi-sélection, utiliser handleMultiSelect
    if (multiSelectMode) {
      return handleMultiSelect(player);
    }

    // Déterminer dans quelle section se trouve ce joueur
    const getPlayerSection = (playerId: number, isGoalie: boolean): TeamSection | null => {
      if (isGoalie) {
        if (mainGoalies.find(g => g.playerid === playerId)) return 'MAIN';
        if (reserveGoalies.find(g => g.playerid === playerId)) return 'RESERVES';
        if (scratchGoalies.find(g => g.playerid === playerId)) return 'SCRATCH';
        if (injuredGoalies.find(g => g.playerid === playerId)) return 'INJURED';
      } else {
        if (mainPlayers.find(p => p.playerid === playerId)) return 'MAIN';
        if (reservePlayers.find(p => p.playerid === playerId)) return 'RESERVES';
        if (scratchPlayers.find(p => p.playerid === playerId)) return 'SCRATCH';
        if (injuredPlayers.find(p => p.playerid === playerId)) return 'INJURED';
      }
      return null;
    };

    const currentPlayerSection = getPlayerSection(player.playerid, !!player.goaliefullname);
    
    if (player.goaliefullname) {
      // Gestion des gardiens
      if (selectedGoalie === player.playerid) {
        setSelectedGoalie(null);
        setSelectedGoalieSection(null);
        return;
      }
      
      // Si on a déjà un gardien sélectionné, préparer l'échange
      if (selectedGoalie && selectedGoalieSection) {
        const selectedGoalieObj = [...mainGoalies, ...reserveGoalies, ...scratchGoalies, ...injuredGoalies]
          .find(g => g.playerid === selectedGoalie);
        
        if (selectedGoalieObj && selectedGoalieSection !== currentPlayerSection) {
          // Déterminer le type d'échange
          let swapType: 'normal' | 'scratch' | 'scratch-reserve' = 'normal';
          let mainGoalie, otherGoalie;
          
          if ((currentPlayerSection === 'MAIN' && selectedGoalieSection === 'RESERVES') ||
              (currentPlayerSection === 'RESERVES' && selectedGoalieSection === 'MAIN')) {
            swapType = 'normal';
            mainGoalie = currentPlayerSection === 'MAIN' ? player : selectedGoalieObj;
            otherGoalie = currentPlayerSection === 'MAIN' ? selectedGoalieObj : player;
          } else if ((currentPlayerSection === 'MAIN' && selectedGoalieSection === 'SCRATCH') ||
                     (currentPlayerSection === 'SCRATCH' && selectedGoalieSection === 'MAIN')) {
            swapType = 'scratch';
            mainGoalie = currentPlayerSection === 'MAIN' ? player : selectedGoalieObj;
            otherGoalie = currentPlayerSection === 'MAIN' ? selectedGoalieObj : player;
          } else if ((currentPlayerSection === 'SCRATCH' && selectedGoalieSection === 'RESERVES') ||
                     (currentPlayerSection === 'RESERVES' && selectedGoalieSection === 'SCRATCH')) {
            swapType = 'scratch-reserve';
            mainGoalie = currentPlayerSection === 'SCRATCH' ? player : selectedGoalieObj;
            otherGoalie = currentPlayerSection === 'SCRATCH' ? selectedGoalieObj : player;
          }
          
          setSwapDetails({
            mainName: mainGoalie.goaliefullname,
            reserveName: otherGoalie.goaliefullname,
            mainId: mainGoalie.playerid,
            reserveId: otherGoalie.playerid,
            isGoalie: true,
            swapType: swapType
          });
          
          setShowSwapModal(true);
          return;
        }
      }
      
      // Sélectionner ce gardien
      setSelectedGoalie(player.playerid);
      setSelectedGoalieSection(currentPlayerSection);
      setSelectedPlayer(null);
      setSelectedPlayerSection(null);
    } else {
      // Gestion des joueurs
      if (selectedPlayer === player.playerid) {
        setSelectedPlayer(null);
        setSelectedPlayerSection(null);
        return;
      }
      
      // Si on a déjà un joueur sélectionné, préparer l'échange
      if (selectedPlayer && selectedPlayerSection) {
        const selectedPlayerObj = [...mainPlayers, ...reservePlayers, ...scratchPlayers, ...injuredPlayers]
          .find(p => p.playerid === selectedPlayer);
        
        if (selectedPlayerObj && selectedPlayerSection !== currentPlayerSection) {
          // Déterminer le type d'échange
          let swapType: 'normal' | 'scratch' | 'scratch-reserve' = 'normal';
          let mainPlayer, otherPlayer;
          
          if ((currentPlayerSection === 'MAIN' && selectedPlayerSection === 'RESERVES') ||
              (currentPlayerSection === 'RESERVES' && selectedPlayerSection === 'MAIN')) {
            swapType = 'normal';
            mainPlayer = currentPlayerSection === 'MAIN' ? player : selectedPlayerObj;
            otherPlayer = currentPlayerSection === 'MAIN' ? selectedPlayerObj : player;
          } else if ((currentPlayerSection === 'MAIN' && selectedPlayerSection === 'SCRATCH') ||
                     (currentPlayerSection === 'SCRATCH' && selectedPlayerSection === 'MAIN')) {
            swapType = 'scratch';
            mainPlayer = currentPlayerSection === 'MAIN' ? player : selectedPlayerObj;
            otherPlayer = currentPlayerSection === 'MAIN' ? selectedPlayerObj : player;
          } else if ((currentPlayerSection === 'SCRATCH' && selectedPlayerSection === 'RESERVES') ||
                     (currentPlayerSection === 'RESERVES' && selectedPlayerSection === 'SCRATCH')) {
            swapType = 'scratch-reserve';
            mainPlayer = currentPlayerSection === 'SCRATCH' ? player : selectedPlayerObj;
            otherPlayer = currentPlayerSection === 'SCRATCH' ? selectedPlayerObj : player;
          }
          
          setSwapDetails({
            mainName: mainPlayer.skaterfullname,
            reserveName: otherPlayer.skaterfullname,
            mainId: mainPlayer.playerid,
            reserveId: otherPlayer.playerid,
            isGoalie: false,
            swapType: swapType
          });
          
          setShowSwapModal(true);
          return;
        }
      }
      
      // Sélectionner ce joueur
      setSelectedPlayer(player.playerid);
      setSelectedPlayerSection(currentPlayerSection);
      setSelectedGoalie(null);
      setSelectedGoalieSection(null);
    }
  };
  
  const handleConfirmSwap = async (): Promise<{ success: boolean }> => {
    if (!swapDetails || !selectedLeague || !currentUserId) return { success: false };
    
    setProcessingSwap(true);
    setSwapError(null);
    let closeModal = true;
    let swapSucceeded = false;
    
    try {
      let result;
      
      // Determine which swap function to use based on swapType
      if (swapDetails.swapType === 'scratch') {
        // Use the scratch swap function for main <-> scratch
        result = await swapPlayerWithScratch(
          selectedLeague.id,
          Number(currentUserId),
          { 
            id: swapDetails.mainId, 
            isGoalie: swapDetails.isGoalie 
          },
          { 
            id: swapDetails.reserveId, 
            isGoalie: swapDetails.isGoalie 
          }
        );
      } else if (swapDetails.swapType === 'scratch-reserve') {
        // Use the new scratch-reserve swap function
        result = await swapScratchWithReserve(
          selectedLeague.id,
          Number(currentUserId),
          { 
            id: swapDetails.mainId, 
            isGoalie: swapDetails.isGoalie 
          },
          { 
            id: swapDetails.reserveId, 
            isGoalie: swapDetails.isGoalie 
          }
        );
      } else {
        // Use the normal swap function for main <-> reserves
        result = await swapPlayers(
          selectedLeague.id,
          Number(currentUserId),
          { 
            id: swapDetails.mainId, 
            isGoalie: swapDetails.isGoalie 
          },
          { 
            id: swapDetails.reserveId, 
            isGoalie: swapDetails.isGoalie 
          }
        );
      }
      
      if (!result.success) {        
        if (result.error.message === 'Demotion limit reached') {
          setSwapError(translate('demotion.limit.reached', language, {
            current: demotionInfo.current.toString(),
            max: demotionInfo.max.toString()
          }));
        } else if (result.error.message?.includes('Reserve salary cap would be exceeded')) {
          setSwapError(language === 'fr' 
            ? 'Impossible d\'effectuer la permutation. Le plafond salarial des réserves serait dépassé.'
            : 'Cannot perform swap. Reserve salary cap would be exceeded.');
        } else if (result.error.message?.includes('Main club salary cap would be exceeded')) {
          setSwapError(language === 'fr' 
            ? 'Impossible d\'effectuer la permutation. Le plafond salarial du club principal serait dépassé.'
            : 'Cannot perform swap. Main club salary cap would be exceeded.');
        } else if (result.error.message) {
          setSwapError(result.error.message);
        } else {
          throw new Error(result.error?.message || result.error);
        }
        closeModal = false;
        return { success: false };
      }
      console.log("here 539");
      toast.success(`Swapped ${swapDetails.mainName} and ${swapDetails.reserveName}`);
      swapSucceeded = true;
      
      // Update demotion counter for normal swaps and scratch-reserve swaps (both count as demotions)
      if (swapDetails.swapType !== 'scratch' && (result as any).demotionInfo) {
        setDemotionInfo((result as any).demotionInfo);
      }
      
      // Update player lists after swap
      if (swapDetails.swapType === 'scratch') {
        // Handle scratch swap - main <-> scratch
        if (swapDetails.isGoalie) {
          const mainGoalie = mainGoalies.find(g => g.playerid === swapDetails.mainId);
          const scratchGoalie = scratchGoalies.find(g => g.playerid === swapDetails.reserveId);
          
          if (mainGoalie && scratchGoalie) {
            // Move main goalie to scratch
            setScratchGoalies([
              ...scratchGoalies.filter(g => g.playerid !== swapDetails.reserveId),
              { ...mainGoalie, is_scratch: true }
            ]);
            
            // Move scratch goalie to main
            setMainGoalies([
              ...mainGoalies.filter(g => g.playerid !== swapDetails.mainId),
              { ...scratchGoalie, is_scratch: false }
            ]);
          }
        } else {
          const mainPlayer = mainPlayers.find(p => p.playerid === swapDetails.mainId);
          const scratchPlayer = scratchPlayers.find(p => p.playerid === swapDetails.reserveId);
          
          if (mainPlayer && scratchPlayer) {
            // Move main player to scratch
            setScratchPlayers([
              ...scratchPlayers.filter(p => p.playerid !== swapDetails.reserveId),
              { ...mainPlayer, is_scratch: true }
            ]);
            
            // Move scratch player to main
            setMainPlayers([
              ...mainPlayers.filter(p => p.playerid !== swapDetails.mainId),
              { ...scratchPlayer, is_scratch: false }
            ]);
          }
        }
      } else if (swapDetails.swapType === 'scratch-reserve') {
        // Handle scratch-reserve swap - scratch <-> reserves
        if (swapDetails.isGoalie) {
          const scratchGoalie = scratchGoalies.find(g => g.playerid === swapDetails.mainId);
          const reserveGoalie = reserveGoalies.find(g => g.playerid === swapDetails.reserveId);
          
          if (scratchGoalie && reserveGoalie) {
            // Move scratch goalie to reserves
            setReserveGoalies([
              ...reserveGoalies.filter(g => g.playerid !== swapDetails.reserveId),
              { ...scratchGoalie, is_scratch: false, isnhl: false }
            ]);
            
            // Move reserve goalie to scratch
            setScratchGoalies([
              ...scratchGoalies.filter(g => g.playerid !== swapDetails.mainId),
              { ...reserveGoalie, is_scratch: true, isnhl: true }
            ]);
          }
        } else {
          const scratchPlayer = scratchPlayers.find(p => p.playerid === swapDetails.mainId);
          const reservePlayer = reservePlayers.find(p => p.playerid === swapDetails.reserveId);
          
          if (scratchPlayer && reservePlayer) {
            // Move scratch player to reserves
            setReservePlayers([
              ...reservePlayers.filter(p => p.playerid !== swapDetails.reserveId),
              { ...scratchPlayer, is_scratch: false, isnhl: false }
            ]);
            
            // Move reserve player to scratch
            setScratchPlayers([
              ...scratchPlayers.filter(p => p.playerid !== swapDetails.mainId),
              { ...reservePlayer, is_scratch: true, isnhl: true }
            ]);
          }
        }
      } else {
        // Handle normal swap - main <-> reserves
        if (swapDetails.isGoalie) {
          const mainGoalie = mainGoalies.find(g => g.playerid === swapDetails.mainId);
          const reserveGoalie = reserveGoalies.find(g => g.playerid === swapDetails.reserveId);
          
          if (mainGoalie && reserveGoalie) {
            setMainGoalies([
              ...mainGoalies.filter(g => g.playerid !== swapDetails.mainId),
              { ...reserveGoalie, isnhl: true }
            ]);
            
            setReserveGoalies([
              ...reserveGoalies.filter(g => g.playerid !== swapDetails.reserveId),
              { ...mainGoalie, isnhl: false }
            ]);
          }
        } else {
          const mainPlayer = mainPlayers.find(p => p.playerid === swapDetails.mainId);
          const reservePlayer = reservePlayers.find(p => p.playerid === swapDetails.reserveId);
          
          if (mainPlayer && reservePlayer) {
            setMainPlayers([
              ...mainPlayers.filter(p => p.playerid !== swapDetails.mainId),
              { ...reservePlayer, isnhl: true }
            ]);
            
            setReservePlayers([
              ...reservePlayers.filter(p => p.playerid !== swapDetails.reserveId),
              { ...mainPlayer, isnhl: false }
            ]);
          }
        }
      }
      
    } catch (error) {
      console.error('Error swapping players:', error);
      toast.error(getErrorMessage(error, 'Impossible d’effectuer cette permutation.', 'Failed to swap players'));
    } finally {
      setProcessingSwap(false);
      if (closeModal) {
        setShowSwapModal(false);
        setSwapDetails(null);
        // Remettre à zéro les sélections après la fermeture de la modal
        setSelectedPlayer(null);
        setSelectedGoalie(null);
        setSelectedPlayerSection(null);
        setSelectedGoalieSection(null);
      }
    }
    return { success: swapSucceeded };
  };

  const handleSendToInjuredList = async (player: any) => {
    if (!currentUserId || !selectedLeague) return;
    setProcessingAction(true);

    try {
      // Vérifier si l'ajout de ce joueur dépasserait la limite de joueurs blessés
      const currentInjuredCount = injuredPlayers.length + injuredGoalies.length;
      const injuryLimit = getInjuryLimit();
      
      if (currentInjuredCount >= injuryLimit) {
        toast.error(`Impossible de placer ce joueur sur LTIR. Limite de ${injuryLimit} joueurs sur LTIR atteinte.`);
        return;
      }

      // Mettre à jour le champ is_injured dans la table league_players_user
      const { error } = await supabase
        .from('league_players_user')
        .update({ is_injured: true })
        .eq('player_id', player.playerid)
        .eq('user_id', Number(currentUserId))
        .eq('league_id', selectedLeague.id!);

      if (error) throw error;

  toast.success(`${player.skaterfullname || player.goaliefullname} a été placé sur LTIR`);

      // Déplacer le joueur vers la liste des blessés dans l'interface
      if (player.goaliefullname) {
        if (activeSection === 'MAIN') {
          setMainGoalies(mainGoalies.filter((p) => p.playerid !== player.playerid));
        } else {
          setReserveGoalies(reserveGoalies.filter((p) => p.playerid !== player.playerid));
        }
        setInjuredGoalies([...injuredGoalies, player]);
      } else {
        if (activeSection === 'MAIN') {
          setMainPlayers(mainPlayers.filter((p) => p.playerid !== player.playerid));
        } else {
          setReservePlayers(reservePlayers.filter((p) => p.playerid !== player.playerid));
        }
        setInjuredPlayers([...injuredPlayers, player]);
      }

      // Attendre 1.5 seconde pour laisser le toast s'afficher avant de rafraîchir
      setTimeout(() => {
        refreshAfterMutation();
      }, 1500);
    } catch (error) {
      console.error('Error sending player to injured list:', error);
  toast.error('Erreur lors du placement du joueur sur LTIR');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReactivateFromInjuredList = async (player: any) => {
    if (!currentUserId || !selectedLeague) return;
    
    // Ouvrir le modal pour choisir le mode de réactivation
    setReactivatePlayerInfo({
      id: player.playerid,
      name: player.skaterfullname || player.goaliefullname,
      isGoalie: !!player.goaliefullname,
      raw: player
    });
    setShowReactivateModal(true);
  };

  // Réactivation directe avec choix de destination
  const handleDirectReactivation = async (destination: 'MAIN' | 'RESERVES') => {
    if (!reactivatePlayerInfo || !currentUserId || !selectedLeague) return { success: false };
    
    const player = reactivatePlayerInfo.raw;
    setProcessingAction(true);

    try {
      const result = await reactivateInjuredPlayerDirect(
        selectedLeague.id,
        Number(currentUserId),
        player.playerid,
        reactivatePlayerInfo.isGoalie,
        destination,
        { enforceSalaryCap: isBpmSession }
      );

      if (!result.success) {
        toast.error(result.error?.message || (language === 'fr' ? 'Erreur lors de la réactivation du joueur' : 'Error reactivating player'));
        return { success: false };
      }

      const destinationText = destination === 'MAIN' 
        ? (language === 'fr' ? 'au club principal' : 'to main club')
        : (language === 'fr' ? 'aux réserves' : 'to reserves');
      
      toast.success(`${player.skaterfullname || player.goaliefullname} ${language === 'fr' ? 'a été réactivé de LTIR' : 'has been reactivated from LTIR'} ${destinationText}`);

      // Déplacer le joueur vers la liste appropriée selon la destination
      if (player.goaliefullname) {
        setInjuredGoalies(injuredGoalies.filter((g) => g.playerid !== player.playerid));
        if (destination === 'MAIN') {
          setMainGoalies([...mainGoalies, { ...player, isnhl: true }]);
        } else {
          setReserveGoalies([...reserveGoalies, { ...player, isnhl: false }]);
        }
      } else {
        setInjuredPlayers(injuredPlayers.filter((p) => p.playerid !== player.playerid));
        if (destination === 'MAIN') {
          setMainPlayers([...mainPlayers, { ...player, isnhl: true }]);
        } else {
          setReservePlayers([...reservePlayers, { ...player, isnhl: false }]);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error reactivating player from injured list:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la réactivation du joueur' : 'Error reactivating player');
      return { success: false };
    } finally {
      setProcessingAction(false);
      setShowReactivateModal(false);
      setReactivatePlayerInfo(null);
    }
  };

  // Réactivation avec swap avec choix de destination
  const handleReactivationWithSwap = async (swapWithPlayerId: number, _swapFromSection: 'MAIN' | 'RESERVES', destination: 'MAIN' | 'RESERVES') => {
    if (!reactivatePlayerInfo || !currentUserId || !selectedLeague) return { success: false };
    
    const player = reactivatePlayerInfo.raw;
    setProcessingAction(true);

    try {
      const result = await reactivateInjuredWithSwap(
        selectedLeague.id,
        Number(currentUserId),
        player.playerid,
        swapWithPlayerId,
        reactivatePlayerInfo.isGoalie,
        destination
      );

      if (!result.success) {
        toast.error(result.error?.message || (language === 'fr' ? 'Erreur lors de la réactivation avec permutation' : 'Error reactivating with swap'));
        return { success: false };
      }

      const destinationText = destination === 'MAIN' 
        ? (language === 'fr' ? 'au club principal' : 'to main club')
        : (language === 'fr' ? 'aux réserves' : 'to reserves');
      
      toast.success(`${player.skaterfullname || player.goaliefullname} ${language === 'fr' ? 'a été réactivé avec permutation' : 'has been reactivated with swap'} ${destinationText} ${language === 'fr' ? '(sans coût de rétrogradation)' : '(no demotion cost)'}`);
      return { success: true };
    } catch (error) {
      console.error('Error reactivating player with swap:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la réactivation avec permutation', 'Error reactivating player with swap'));
      return { success: false };
    } finally {
      setProcessingAction(false);
      setShowReactivateModal(false);
      setReactivatePlayerInfo(null);
    }
  };

  // Standard Flexible : réactivation en remplaçant un joueur (équipe pleine), sans signature ni rétrogradation
  const handleReactivationReplacingPlayer = async (replacedPlayerId: number) => {
    if (!reactivatePlayerInfo || !currentUserId || !selectedLeague) return { success: false };

    const player = reactivatePlayerInfo.raw;
    const replaced = [...mainPlayers, ...mainGoalies].find((p) => p.playerid === replacedPlayerId);
    const replacedName = replaced?.skaterfullname || replaced?.goaliefullname || '';
    setProcessingAction(true);

    try {
      const result = await reactivateInjuredReplacingPlayer(
        selectedLeague.id,
        Number(currentUserId),
        player.playerid,
        replacedPlayerId,
        reactivatePlayerInfo.isGoalie
      );

      if (!result.success) {
        toast.error(result.error?.message || (language === 'fr' ? 'Erreur lors de la réactivation du joueur' : 'Error reactivating player'));
        return { success: false };
      }

      if (result.outcome === 'RELEASED') {
        await removeFromProtectionList(replacedPlayerId, Number(currentUserId), selectedLeague.id);
      }

      const injuredName = player.skaterfullname || player.goaliefullname;
      const outcomeText = result.outcome === 'RELEASED'
        ? (language === 'fr' ? 'a été libéré' : 'has been released')
        : (language === 'fr' ? 'a été envoyé en réserve' : 'has been sent to reserves');
      toast.success(
        language === 'fr'
          ? `${injuredName} a été réactivé. ${replacedName} ${outcomeText} (aucune signature ni rétrogradation).`
          : `${injuredName} has been reactivated. ${replacedName} ${outcomeText} (no signing or demotion).`
      );
      return { success: true };
    } catch (error) {
      console.error('Error reactivating player by replacing a player:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la réactivation du joueur', 'Error reactivating player'));
      return { success: false };
    } finally {
      setProcessingAction(false);
      setShowReactivateModal(false);
      setReactivatePlayerInfo(null);
    }
  };

  const handleMoveToScratch = async (player: any) => {
    if (!currentUserId || !selectedLeague) return;
    setProcessingAction(true);

    try {
      // Vérifier si le joueur est déjà dans les scratch
      if (scratchPlayers.some(p => p.playerid === player.playerid)) {
        toast.error('Ce joueur est déjà dans la section scratch');
        return;
      }

      // Vérifier la limite de scratch (joueurs + gardiens ensemble)
      const currentTotalScratchCount = scratchPlayers.length + scratchGoalies.length;
      const scratchLimit = getScratchLimit();
      
      if (currentTotalScratchCount >= scratchLimit) {
        toast.error(`Impossible d'ajouter ce joueur. Vous avez atteint la limite de ${scratchLimit} scratch players/goalies autorisés par la ligue.`);
        return;
      }

      // Mettre à jour le champ is_scratch dans la table league_players_user
      const { success, error } = await movePlayerToScratch(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (!success) throw error;

      toast.success(`${player.skaterfullname} a été déplacé vers les scratch players`);

      // Déplacer le joueur vers la liste des scratch dans l'interface
      setMainPlayers(mainPlayers.filter((p) => p.playerid !== player.playerid));
      setScratchPlayers([...scratchPlayers, player]);

      // Recharger les plafonds salariaux pour mettre à jour l'affichage
      const { data: mainCapData, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', true)
        .single();

      if (!mainCapError) {
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);
      }

      const { data: reserveCapData, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', false)
        .single();

      if (!reserveCapError) {
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      }
    } catch (error) {
      console.error('Error moving player to scratch:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du déplacement du joueur vers les scratch.', 'Failed to move player to scratch'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRemoveFromScratch = async (player: any) => {
    if (!currentUserId || !selectedLeague || !leagueSettings) return;
    setProcessingAction(true);

    try {
      // Vérifier les limites d'effectif avant de remettre le joueur au club principal
      const { data: rosterCounts, error: rosterError } = await supabase
        .from('get_number_type_player_per_team')
        .select('*')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .single();

      if (rosterError && rosterError.code !== 'PGRST116') throw rosterError;

      const currentCounts = rosterCounts || { number_of_defenders: 0, number_of_fowards: 0 };
      const isDefenseman = player.positioncode === 'D';

      // Vérifier si le retour du joueur dépasserait les limites d'effectif
      if (isDefenseman) {
        if (currentCounts.number_of_defenders + 1 > leagueSettings.number_def) {
          toast.error(translate('cannot.sign.defender', language, {
            limit: leagueSettings.number_def.toString()
          }));
          return;
        }
      } else if (['C', 'L', 'R'].includes(player.positioncode)) {
        if (currentCounts.number_of_fowards + 1 > leagueSettings.number_foward) {
          toast.error(translate('cannot.sign.forward', language, {
            limit: leagueSettings.number_foward.toString()
          }));
          return;
        }
      }

      // Mettre à jour le champ is_scratch dans la table league_players_user
      const { success, error } = await removePlayerFromScratch(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (!success) throw error;

      toast.success(`${player.skaterfullname} a été retiré des scratch players`);

      // Déplacer le joueur vers la liste principale dans l'interface
      setScratchPlayers(scratchPlayers.filter((p) => p.playerid !== player.playerid));
      setMainPlayers([...mainPlayers, player]);

      // Recharger les plafonds salariaux pour mettre à jour l'affichage
      const { data: mainCapData, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', true)
        .single();

      if (!mainCapError) {
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);
      }

      const { data: reserveCapData, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', false)
        .single();

      if (!reserveCapError) {
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      }
    } catch (error) {
      console.error('Error removing player from scratch:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du retrait du joueur des scratch.', 'Failed to remove player from scratch'));
    } finally {
      setProcessingAction(false);
    }
  };

  // Fonction utilitaire pour mettre à jour l'état local d'un joueur
  const updatePlayerLocalState = (playerId: number, isGoalie: boolean, updates: any) => {
    const updateFunction = (players: any[]) => 
      players.map(p => p.playerid === playerId ? { ...p, ...updates } : p);

    if (isGoalie) {
      setMainGoalies(updateFunction);
      setReserveGoalies(updateFunction);
      setScratchGoalies(updateFunction);
      setInjuredGoalies(updateFunction);
    } else {
      setMainPlayers(updateFunction);
      setReservePlayers(updateFunction);
      setScratchPlayers(updateFunction);
      setInjuredPlayers(updateFunction);
    }
  };

  // Handler pour gérer le statut Trading Bloc des joueurs
  const handleToggleTradingBloc = async (player: any, status: boolean) => {
    if (!currentUserId || !selectedLeague) return;

    // Mettre à jour immédiatement l'état local pour un feedback visuel instantané
    const isGoalie = !!player.goaliefullname;
    updatePlayerLocalState(player.playerid, isGoalie, { trading_bloc: status });

    try {
      const result = await updatePlayerTradingStatus(
        selectedLeague.id,
        player.playerid,
        Number(currentUserId),
        status
      );

      if (result.success) {
        toast.success(
          status 
            ? `${player.skaterfullname || player.goaliefullname} ajouté au Trading Bloc (Disponible)` 
            : `${player.skaterfullname || player.goaliefullname} retiré du Trading Bloc`
        );
      } else {
        // En cas d'erreur, revert l'état local
        updatePlayerLocalState(player.playerid, isGoalie, { trading_bloc: !status });
        throw result.error;
      }
    } catch (error) {
      // En cas d'erreur, revert l'état local
      updatePlayerLocalState(player.playerid, isGoalie, { trading_bloc: !status });
      console.error('Error updating trading bloc status:', error);
      toast.error('Erreur lors de la mise à jour du statut Trading Bloc');
    }
  };

  // Handler pour gérer le statut Intouchable des joueurs
  const handleToggleIntouchable = async (player: any, isIntouchable: boolean) => {
    if (!currentUserId || !selectedLeague) return;

    // Mettre à jour immédiatement l'état local pour un feedback visuel instantané
    const isGoalie = !!player.goaliefullname;
    updatePlayerLocalState(player.playerid, isGoalie, { is_intouchable: isIntouchable });

    try {
      const result = await updatePlayerIntouchableStatus(
        selectedLeague.id,
        player.playerid,
        Number(currentUserId),
        isIntouchable
      );

      if (result.success) {
        toast.success(
          isIntouchable 
            ? `${player.skaterfullname || player.goaliefullname} marqué comme Intouchable` 
            : `${player.skaterfullname || player.goaliefullname} retiré des Intouchables`
        );
      } else {
        // En cas d'erreur, revert l'état local
        updatePlayerLocalState(player.playerid, isGoalie, { is_intouchable: !isIntouchable });
        throw result.error;
      }
    } catch (error) {
      // En cas d'erreur, revert l'état local
      updatePlayerLocalState(player.playerid, isGoalie, { is_intouchable: !isIntouchable });
      console.error('Error updating intouchable status:', error);
      toast.error('Erreur lors de la mise à jour du statut Intouchable');
    }
  };

  const handleMoveGoalieToScratch = async (goalie: any) => {
    if (!currentUserId || !selectedLeague) return;
    setProcessingAction(true);

    try {
      // Vérifier si le gardien est déjà dans les scratch
      if (scratchGoalies.some(g => g.playerid === goalie.playerid)) {
        toast.error('Ce gardien est déjà dans la section scratch');
        return;
      }

      // Vérifier la limite de scratch (joueurs + gardiens ensemble)
      const currentTotalScratchCount = scratchPlayers.length + scratchGoalies.length;
      const scratchLimit = getScratchLimit();
      
      if (currentTotalScratchCount >= scratchLimit) {
        toast.error(`Impossible d'ajouter ce gardien. Vous avez atteint la limite de ${scratchLimit} scratch players/goalies autorisés par la ligue.`);
        return;
      }

      // Mettre à jour le champ is_scratch dans la table league_players_user
      const { success, error } = await movePlayerToScratch(
        goalie.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (!success) throw error;

      toast.success(`${goalie.goaliefullname} a été déplacé vers les scratch players`);

      // Déplacer le gardien vers la liste des scratch dans l'interface
      setMainGoalies(mainGoalies.filter((g) => g.playerid !== goalie.playerid));
      setScratchGoalies([...scratchGoalies, goalie]);

      // Recharger les plafonds salariaux pour mettre à jour l'affichage
      const { data: mainCapData, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', true)
        .single();

      if (!mainCapError) {
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);
      }

      const { data: reserveCapData, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', false)
        .single();

      if (!reserveCapError) {
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      }
    } catch (error) {
      console.error('Error moving goalie to scratch:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du déplacement du gardien vers les scratch.', 'Failed to move goalie to scratch'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRemoveGoalieFromScratch = async (goalie: any) => {
    if (!currentUserId || !selectedLeague || !leagueSettings) return;
    setProcessingAction(true);

    try {
      // Vérifier les limites d'effectif avant de remettre le gardien au club principal
      const { data: rosterCounts, error: rosterError } = await supabase
        .from('get_number_type_goalie_per_team')
        .select('*')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .single();

      if (rosterError && rosterError.code !== 'PGRST116') throw rosterError;

      const currentGoalieCount = rosterCounts?.number_of_goalie || 0;
      
      // Vérifier si le retour du gardien dépasserait les limites d'effectif
      if (currentGoalieCount + 1 > leagueSettings.number_goalie) {
        toast.error(language === 'fr' 
          ? `Impossible de remettre ce gardien au club principal. Limite de gardiens atteinte (${leagueSettings.number_goalie}).`
          : `Cannot return this goalie to main club. Goalie limit reached (${leagueSettings.number_goalie}).`
        );
        return;
      }

      // Mettre à jour le champ is_scratch dans la table league_players_user
      const { success, error } = await removePlayerFromScratch(
        goalie.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (!success) throw error;

      toast.success(`${goalie.goaliefullname} a été retiré des scratch players`);

      // Déplacer le gardien vers la liste principale dans l'interface
      setScratchGoalies(scratchGoalies.filter((g) => g.playerid !== goalie.playerid));
      setMainGoalies([...mainGoalies, goalie]);

      // Recharger les plafonds salariaux pour mettre à jour l'affichage
      const { data: mainCapData, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', true)
        .single();

      if (!mainCapError) {
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);
      }

      const { data: reserveCapData, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUserId)
        .eq('isnhl', false)
        .single();

      if (!reserveCapError) {
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      }
    } catch (error) {
      console.error('Error removing goalie from scratch:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du retrait du gardien des scratch.', 'Failed to remove goalie from scratch'));
    } finally {
      setProcessingAction(false);
    }
  };

  // Gestionnaires pour le modal de promotion
  const handlePromoteToMainClub = async () => {
    if (!promotePlayerInfo || !currentUserId || !selectedLeague) return { success: false };
    try {
      const result = await promotePlayerFromReserves(
        promotePlayerInfo.id,
        Number(currentUserId),
        selectedLeague.id,
        'main_club',
        promotePlayerInfo.isGoalie
      );
      if (!result.success) {
        const errorMessage = result.error?.message || (language === 'fr' 
          ? 'Erreur lors de la promotion du joueur.'
          : 'Error promoting player.');
        toast.error(errorMessage);
        return { success: false };
      }
      toast.success(language === 'fr' 
        ? `${promotePlayerInfo.name} promu au club principal!`
        : `${promotePlayerInfo.name} promoted to main club!`);
      if (promotePlayerInfo.isGoalie) {
        const player = reserveGoalies.find(p => p.playerid === promotePlayerInfo.id);
        if (player) {
          setReserveGoalies(reserveGoalies.filter(p => p.playerid !== promotePlayerInfo.id));
          setMainGoalies([...mainGoalies, { ...player, isnhl: true }]);
        }
      } else {
        const player = reservePlayers.find(p => p.playerid === promotePlayerInfo.id);
        if (player) {
          setReservePlayers(reservePlayers.filter(p => p.playerid !== promotePlayerInfo.id));
          setMainPlayers([...mainPlayers, { ...player, isnhl: true }]);
        }
      }
      return { success: true };
    } catch (error) {
      console.error('Error promoting to main club:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la promotion du joueur.', 'Error promoting player.'));
      return { success: false };
    } finally {
      setShowPromoteModal(false);
      setPromotePlayerInfo(null);
    }
  };

  const handlePromoteToScratch = async () => {
    if (!promotePlayerInfo || !currentUserId || !selectedLeague) return { success: false };
    try {
      const result = await promotePlayerFromReserves(
        promotePlayerInfo.id,
        Number(currentUserId),
        selectedLeague.id,
        'scratch',
        promotePlayerInfo.isGoalie
      );
      if (!result.success) {
        const errorMessage = result.error?.message || (language === 'fr' 
          ? 'Erreur lors de la promotion du joueur.'
          : 'Error promoting player.');
        toast.error(errorMessage);
        return { success: false };
      }
      toast.success(language === 'fr' 
        ? `${promotePlayerInfo.name} promu aux scratch players!`
        : `${promotePlayerInfo.name} promoted to scratch players!`);
      if (promotePlayerInfo.isGoalie) {
        const player = reserveGoalies.find(p => p.playerid === promotePlayerInfo.id);
        if (player) {
          setReserveGoalies(reserveGoalies.filter(p => p.playerid !== promotePlayerInfo.id));
          setScratchGoalies([...scratchGoalies, { ...player, isnhl: true, isscratch: true }]);
        }
      } else {
        const player = reservePlayers.find(p => p.playerid === promotePlayerInfo.id);
        if (player) {
          setReservePlayers(reservePlayers.filter(p => p.playerid !== promotePlayerInfo.id));
          setScratchPlayers([...scratchPlayers, { ...player, isnhl: true, isscratch: true }]);
        }
      }
      return { success: true };
    } catch (error) {
      console.error('Error promoting to scratch:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la promotion du joueur.', 'Error promoting player.'));
      return { success: false };
    } finally {
      setShowPromoteModal(false);
      setPromotePlayerInfo(null);
    }
  };

  const handleClosePromoteModal = () => {
    setShowPromoteModal(false);
    setPromotePlayerInfo(null);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setItemsPerPage(newValue);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const filteredPlayers = (activeSection === 'MAIN'
    ? mainPlayers
    : activeSection === 'RESERVES'
    ? reservePlayers
    : injuredPlayers
  ).filter((player) => {
    switch (selectedPosition) {
      case 'FORWARD':
        return ['C', 'L', 'R'].includes(player.positioncode);
      case 'DEFENSE':
        return player.positioncode === 'D';
      case 'C':
      case 'L':
      case 'R':
      case 'D':
        return player.positioncode === selectedPosition;
      default:
        return true;
    }
  });

  const filteredScratchPlayers = scratchPlayers.filter((player) => {
    switch (selectedPosition) {
      case 'FORWARD':
        return ['C', 'L', 'R'].includes(player.positioncode);
      case 'DEFENSE':
        return player.positioncode === 'D';
      case 'C':
      case 'L':
      case 'R':
      case 'D':
        return player.positioncode === selectedPosition;
      default:
        return true;
    }
  });

  const filteredGoalies = (
    activeSection === 'MAIN'
      ? mainGoalies
      : activeSection === 'RESERVES'
      ? reserveGoalies
      : activeSection === 'SCRATCH'
      ? scratchGoalies
      : injuredGoalies
  );

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  const sortedScratchPlayers = [...filteredScratchPlayers].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  const sortedGoalies = [...filteredGoalies].sort((a, b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return goalieSortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return goalieSortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  const sortedMainGoalies = [...mainGoalies].sort((a, b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return goalieSortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return goalieSortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });
  const sortedScratchGoaliesOverview = [...scratchGoalies].sort((a, b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return goalieSortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return goalieSortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });
  const sortedReserveGoalies = [...reserveGoalies].sort((a, b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return goalieSortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return goalieSortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });
  const sortedInjuredGoalies = [...injuredGoalies].sort((a, b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return goalieSortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return goalieSortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  // Grouping helpers for Overview (players only): Forwards vs Defense
  const isForward = (p: any) => ['C', 'L', 'R'].includes(p.positioncode);
  const isDefense = (p: any) => p.positioncode === 'D';

  // Main club grouped
  const sortedMainForwards = [...mainPlayers.filter(isForward)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  const sortedMainDefense = [...mainPlayers.filter(isDefense)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });

  // Scratch grouped
  const sortedScratchForwardsOverview = [...scratchPlayers.filter(isForward)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  const sortedScratchDefenseOverview = [...scratchPlayers.filter(isDefense)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });

  // Reserves grouped
  const sortedReserveForwards = [...reservePlayers.filter(isForward)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  const sortedReserveDefense = [...reservePlayers.filter(isDefense)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });

  // Injured grouped
  const sortedInjuredForwards = [...injuredPlayers.filter(isForward)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  const sortedInjuredDefense = [...injuredPlayers.filter(isDefense)].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });

  const totalPages = Math.ceil(
    (activeTab === 'players' 
      ? sortedPlayers.length 
      : activeTab === 'goalies' 
      ? sortedGoalies.length 
      : activeTab === 'scratches' 
      ? sortedScratchPlayers.length 
      : activeTab === 'scratchGoalies'
      ? sortedGoalies.length
      : activeTab === 'overview'
      ? 1 // Overview doesn't use pagination, just show 1 page
      : 0) / itemsPerPage
  );
  
  // Reset to first page when changing filters, sections, or sort configuration
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPosition, activeSection, activeTab, sortConfig, goalieSortConfig]);

  // Reset to first page when changing items per page
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const paginatedPlayers = sortedPlayers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginatedScratchPlayers = sortedScratchPlayers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginatedGoalies = sortedGoalies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Fonction pour obtenir la limite de blessés avec gestion correcte de 0
  const getInjuryLimit = () => {
    // Si max_injury est défini, y compris 0, utiliser cette valeur
    // Sinon utiliser 5 comme valeur par défaut
    return leagueSettings?.max_injury != null ? leagueSettings.max_injury : 5;
  };

  // Fonction pour obtenir la limite de scratch players avec gestion correcte de 0
  const getScratchLimit = () => {
    // Si number_scratch est défini, y compris 0, utiliser cette valeur
    // Sinon utiliser 3 comme valeur par défaut
    return leagueSettings?.number_scratch != null ? leagueSettings.number_scratch : 3;
  };

  // Calculs pour détecter les dépassements d'alignement / masse salariale
  const mainForwardsCount = mainPlayers.filter(isForward).length;
  const mainDefenseCount = mainPlayers.filter(isDefense).length;
  const mainGoaliesCount = mainGoalies.length;
  const reserveTotalCount = reservePlayers.length + reserveGoalies.length;

  const mainSalaryExceeded = !!(leagueSettings && leagueSettings.max_salary_cap != null && (currentSalaryCap || 0) > leagueSettings.max_salary_cap);
  const reserveSalaryExceeded = !!(leagueSettings && leagueSettings.max_salary_cap_reserve != null && (currentReserveSalaryCap || 0) > leagueSettings.max_salary_cap_reserve);

  // roster exceed checks are evaluated inline when building alignmentIssues

  const alignmentIssues: string[] = [];
  if (leagueSettings) {
    if (mainSalaryExceeded) {
      alignmentIssues.push(
        `Plafond salarial club principal dépassé de ${formatCurrency((currentSalaryCap || 0) - (leagueSettings.max_salary_cap || 0))}`
      );
    }
    if (reserveSalaryExceeded) {
      alignmentIssues.push(
        `Plafond salarial club école dépassé de ${formatCurrency((currentReserveSalaryCap || 0) - (leagueSettings.max_salary_cap_reserve || 0))}`
      );
    }
    if (mainForwardsCount > (leagueSettings.number_foward || 0)) {
      alignmentIssues.push(`Attaquants: ${mainForwardsCount}/${leagueSettings.number_foward}`);
    }
    if (mainForwardsCount < (leagueSettings.number_foward || 0)) {
      alignmentIssues.push(`Attaquants: ${mainForwardsCount}/${leagueSettings.number_foward} (manque ${(leagueSettings.number_foward || 0) - mainForwardsCount})`);
    }
    if (mainDefenseCount > (leagueSettings.number_def || 0)) {
      alignmentIssues.push(`Défenseurs: ${mainDefenseCount}/${leagueSettings.number_def}`);
    }
    if (mainDefenseCount < (leagueSettings.number_def || 0)) {
      alignmentIssues.push(`Défenseurs: ${mainDefenseCount}/${leagueSettings.number_def} (manque ${(leagueSettings.number_def || 0) - mainDefenseCount})`);
    }
    if (mainGoaliesCount > (leagueSettings.number_goalie || 0)) {
      alignmentIssues.push(`Gardiens: ${mainGoaliesCount}/${leagueSettings.number_goalie}`);
    }
    if (mainGoaliesCount < (leagueSettings.number_goalie || 0)) {
      alignmentIssues.push(`Gardiens: ${mainGoaliesCount}/${leagueSettings.number_goalie} (manque ${(leagueSettings.number_goalie || 0) - mainGoaliesCount})`);
    }
    if (reserveTotalCount > (leagueSettings.number_reserve || 0)) {
      alignmentIssues.push(`Club école: ${reserveTotalCount}/${leagueSettings.number_reserve}`);
    }
  }

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className={`card shadow-lg border-0${isEmbed ? ' my-team-embed' : ''}`}>
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary\" role="status">
            <span className="visually-hidden">{translate('loading.standings', language)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MyTeamsModalProvider enabled={anchoredModals}>
    <div id="myteams-page" ref={pageRef} tabIndex={-1} className={`card shadow-lg border-0${isEmbed ? ' my-team-embed' : ''}`}>
      <div className="card-body p-3 p-md-5">
        <OfficeBackButton visible={isBaseStandardUi} />
        <EmbedBackButton />
        {/* Header: logo NHL + nom d'équipe */}
        {(nhlTeamAbbrev || username) && (
          <div className="d-flex align-items-center gap-3 mb-4">
            {nhlTeamAbbrev && <TeamLogo abbrev={nhlTeamAbbrev} size={56} />}
            {username && (
              <div>
                <h4 className="mb-0 fw-bold">{username}</h4>
                {selectedLeague && (
                  <small className="text-muted">{selectedLeague.name}</small>
                )}
              </div>
            )}
          </div>
        )}
        {isStandardFlexible && isBuildPhase && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
            <span style={{ fontSize: '1.4rem', lineHeight: 1 }}></span>
            <div>
              <strong>
                {language === 'fr'
                  ? 'Votre équipe n\'est pas encore complétée !'
                  : 'Your team is not completed yet!'}
              </strong>
              <div className="mt-1" style={{ fontSize: '0.9rem' }}>
                {language === 'fr'
                  ? 'Vous devez compléter votre équipe dans la page « Bâtir mon équipe » pour commencer à accumuler des points.'
                  : 'You must complete your team on the « Build My Team » page to start earning points.'}
              </div>
              <button
                className="btn btn-warning btn-sm mt-2"
                onClick={() => navigate('/build-my-team')}
              >
                {language === 'fr' ? '🏒 Aller à Bâtir mon équipe' : '🏒 Go to Build My Team'}
              </button>
            </div>
          </div>
        )}
        {/* Modal tutoriel — s'affiche uniquement en mode BPM */}
        {showInjuryTutorialModal && (
          <MyTeamsModal onClose={closeInjuryTutorialModal}>
          <div
            data-team-modal-overlay
            role="dialog"
            aria-modal="true"
            aria-label={language === 'fr' ? 'Aide — Liste des blessés' : 'Help — Injured List'}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9000,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)',
              padding: '1rem',
              paddingTop: '4.5rem'
            }}
          >
            <div data-team-modal-card style={{ backgroundColor: theme === 'dark' ? '#0b1220' : '#fff', borderRadius: 12, padding: '1.25rem', maxWidth: 540, width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h5 style={{ margin: 0 }}>{language === 'fr' ? 'Info' : 'Info'}</h5>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeInjuryTutorialModal}>{language === 'fr' ? "Ne plus afficher" : 'Do not show again'}</button>
              </div>
              <div style={{ fontSize: '0.95rem', color: theme === 'dark' ? '#cbd5e1' : '#334155' }}>
                {language === 'fr' ? (
                  <>
                    <p>Pour libérer un joueur, appuyez d'abord sur la <strong>flèche</strong> à droite de son nom, puis choisissez l'action voulue.</p>
                    <p>Pour placer un joueur sur la <strong>liste des blessés</strong>, appuyez aussi sur la flèche à droite de son nom. Cette option est disponible uniquement si le joueur est blessé (icône de blessure affichée à côté de son nom).</p>
                    <p>Vous pouvez ensuite signer un autre joueur depuis la section <strong>Agents libres</strong> pour le remplacer temporairement.</p>
                    <p>Quand le joueur blessé revient au jeu, réactivez-le et remettez-le dans votre alignement sans problème.</p>                    
                  </>
                ) : (
                  <>
                    <p>To release a player, first click the <strong>arrow</strong> next to their name, then choose the desired action.</p>
                    <p>To place a player on the <strong>injured list</strong>, click the arrow next to their name. This option is only available when the player is injured (injury icon shown beside their name).</p>
                    <p>You can sign another player from <strong>Free Agents</strong> to replace them temporarily.</p>
                    <p>When the injured player returns, reactivate them and put them back into your lineup.</p>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: 8 }}>
                <button type="button" className="btn btn-primary" onClick={() => setShowInjuryTutorialModal(false)}>{language === 'fr' ? "J'ai compris" : "Got it"}</button>
              </div>
            </div>
          </div>
          </MyTeamsModal>
        )}
        {leagueSettings && (isTslhTenant() || leagueSettings.count_free_agent_signings === false) && (
          <div className="alert alert-info d-flex align-items-start mb-4" role="alert">
            <Info size={18} className="me-2 mt-1 flex-shrink-0" />
            <div>
              {isTslhTenant() ? (
                <p className="small mb-0">
                  {translate('tslh.signing.limit.intro', language)}{' '}
                  <strong>{translate('tslh.signing.limit.count', language)}</strong>
                  {translate('tslh.signing.limit.instructions', language)}{' '}
                  <strong>{translate('tslh.signing.limit.section', language)}</strong>.
                </p>
              ) : (
                <>
                  <strong className="d-block mb-1">
                    {(() => {
                      const rawDate = leagueSettings.pool_start_date;
                      const parsed = rawDate ? new Date(rawDate) : null;
                      if (parsed && !isNaN(parsed.getTime())) {
                        const formatted = parsed.toLocaleDateString(
                          language === 'fr' ? 'fr-CA' : 'en-CA',
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        );
                        return language === 'fr'
                          ? `Signatures d'agents libres(a condition de respecter les limites d'aliignement vous pouvez par exemple libérer un joueur de votre alignement pour signer un agent libre) illimitées jusqu'au ${formatted}`
                          : `Unlimited free agent signings until ${formatted}`;
                      }
                      return translate('unlimited.fa.signings.title', language);
                    })()}
                  </strong>
                  <span className="small">
                    {translate('unlimited.fa.signings.message', language)}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        {leagueSettings && (
          <div className="card mb-4 border-0 bg-light">
            <div className="card-body p-2">
              <div 
                className="d-flex align-items-center justify-content-between mb-1 cursor-pointer flex-nowrap gap-2" 
                onClick={() => setShowLeagueInfo(!showLeagueInfo)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center min-w-0 flex-grow-1">
                  <Info size={16} className="text-primary me-2" />
                  <h5
                    className="mb-0 text-truncate"
                    style={{ maxWidth: '100%', fontSize: '0.85rem', lineHeight: 1.1 }}
                  >
                    {language === 'fr' ? 'Info. ligue/cap' : 'League info/cap'}
                  </h5>
                </div>
                <button 
                  className="btn btn-sm btn-outline-primary flex-shrink-0 py-0 px-2"
                  type="button"
                  style={{ fontSize: '0.75rem', lineHeight: 1.1 }}
                >
                  {showLeagueInfo 
                    ? (language === 'fr' ? 'Masquer' : 'Hide') 
                    : (language === 'fr' ? 'Afficher' : 'Show')}
                </button>
              </div>
              {showLeagueInfo && (
              <>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{translate('roster.limits', language)}</h6>
                      <ul className="list-unstyled mb-0">
                        <li>{translate('forwards', language)}: {leagueSettings.number_foward}</li>
                        <li>{translate('defensemen', language)}: {leagueSettings.number_def}</li>
                        <li>{translate('goalies', language)}: {leagueSettings.number_goalie}</li>
                        <li>{translate('reserve.players', language)}: {leagueSettings.number_reserve}</li>
                        <li>Scratch players: {leagueSettings.number_scratch || 0}</li>
                      </ul>
                    </div>
                  </div>
                </div>
                {leagueSettings.salary_cap && (
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title d-flex align-items-center">
                          <DollarSign size={18} className="me-2 text-primary" />
                          {translate('main.club.salary.cap', language)}
                        </h6>
                        <div className="d-flex flex-column">
                          <p className="mb-2">
                            {formatCurrency(currentSalaryCap || 0)} / {formatCurrency(leagueSettings.max_salary_cap)}
                          </p>
                          <p className="text-muted mb-0">
                            {translate('available', language)}: {formatCurrency(leagueSettings.max_salary_cap - (currentSalaryCap || 0))}
                          </p>
                          <div className="progress mt-2" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${
                                (currentSalaryCap || 0) > leagueSettings.max_salary_cap
                                  ? 'bg-danger'
                                  : 'bg-success'
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  ((currentSalaryCap || 0) / leagueSettings.max_salary_cap) * 100,
                                  100
                                )}%`,
                              }}
                              aria-valuenow={currentSalaryCap || 0}
                              aria-valuemin={0}
                              aria-valuemax={leagueSettings.max_salary_cap}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {leagueSettings.salary_cap_reserve && (
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title d-flex align-items-center">
                          <DollarSign size={18} className="me-2 text-primary" />
                          {translate('reserve.team.salary.cap', language)}
                        </h6>
                        <div className="d-flex flex-column">
                          <p className="mb-2">
                            {formatCurrency(currentReserveSalaryCap || 0)} / {formatCurrency(leagueSettings.max_salary_cap_reserve)}
                          </p>
                          <p className="text-muted mb-0">
                            {translate('available', language)}: {formatCurrency(leagueSettings.max_salary_cap_reserve - (currentReserveSalaryCap || 0))}
                          </p>
                          <div className="progress mt-2" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${
                                (currentReserveSalaryCap || 0) > leagueSettings.max_salary_cap_reserve
                                  ? 'bg-danger'
                                  : 'bg-success'
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  ((currentReserveSalaryCap || 0) / leagueSettings.max_salary_cap_reserve) * 100,
                                  100
                                )}%`,
                              }}
                              aria-valuenow={currentReserveSalaryCap || 0}
                              aria-valuemin={0}
                              aria-valuemax={leagueSettings.max_salary_cap_reserve}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="row g-3 mt-3">
                {!hideDemotionCard && (
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title">{translate('demotions.title', language)}</h6>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>{translate('season.maximum', language)}:</span>
                          <span className="fw-bold">{demotionInfo.max}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{translate('used.demotions', language)}:</span>
                          <span className={`fw-bold ${demotionInfo.current >= demotionInfo.max ? 'text-danger' : ''}`}>
                            {demotionInfo.current}
                          </span>
                        </div>
                        <div className="progress mt-2" style={{ height: '8px' }}>
                          <div
                            className={`progress-bar ${
                              demotionInfo.current >= demotionInfo.max ? 'bg-danger' : 'bg-success'
                            }`}
                            role="progressbar"
                            style={{ width: `${(demotionInfo.current / demotionInfo.max) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">Signatures</h6>
                      {isTslhTenant() ? (
                        <>
                          <div
                            className={`fw-bold fs-4 lh-1 mb-2 ${
                              signingInfo.max > 0 && signingInfo.current >= signingInfo.max
                                ? 'text-danger'
                                : signingInfo.max > 0 && signingInfo.current >= signingInfo.max - 1
                                ? 'text-warning'
                                : 'text-success'
                            }`}
                          >
                            {signingInfo.current} / {signingInfo.max}
                          </div>
                          <p className="mb-0 small text-muted">
                            <strong>{translate('tslh.signing.limit.count', language)}</strong>
                          </p>
                        </>
                      ) : leagueSettings?.count_free_agent_signings === false ? (
                        <>
                          <div className="d-flex align-items-center mb-2">
                            <span className="badge bg-success">
                              {translate('unlimited.fa.signings.card.title', language)}
                            </span>
                          </div>
                          <p className="mb-0 small text-muted">
                            {translate('unlimited.fa.signings.card.subtitle', language)}
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span>{translate('season.maximum', language)}:</span>
                            <span className="fw-bold">{signingInfo.max}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span>{translate('used.demotions', language)}:</span>
                            <span className={`fw-bold ${signingInfo.current >= signingInfo.max ? 'text-danger' : ''}`}>
                              {signingInfo.current}
                            </span>
                          </div>
                          <div className="progress mt-2" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${
                                signingInfo.current >= signingInfo.max ? 'bg-danger' : 'bg-success'
                              }`}
                              role="progressbar"
                              style={{ width: `${(signingInfo.current / signingInfo.max) * 100}%` }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title d-flex align-items-center">
                        <Bandage size={18} className="me-2 text-danger" />
                        Liste des blessés
                      </h6>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Limite:</span>
                        <span className="fw-bold">{getInjuryLimit()}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Utilisés:</span>
                        <span className={`fw-bold ${(injuredPlayers.length + injuredGoalies.length) >= getInjuryLimit() ? 'text-danger' : ''}`}>
                          {injuredPlayers.length + injuredGoalies.length}
                        </span>
                      </div>
                      <div className="progress mt-2" style={{ height: '8px' }}>
                        <div
                          className={`progress-bar ${
                            (injuredPlayers.length + injuredGoalies.length) >= getInjuryLimit() ? 'bg-danger' : 'bg-success'
                          }`}
                          role="progressbar"
                          style={{ width: `${((injuredPlayers.length + injuredGoalies.length) / getInjuryLimit()) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Salary Retention Debt Card - Only show if there is retention debt and salary cap is enabled */}
              {leagueSettings.salary_cap && retentionDebt > 0 && (
                <div className="row g-3 mt-3">
                  <div className="col-12">
                    <div className="card border-warning">
                      <div className="card-body bg-warning bg-opacity-10">
                        <h6 className="card-title d-flex align-items-center text-warning">
                          <DollarSign size={20} className="me-2" />
                          {translate('salary.retention.debt', language)}
                        </h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fs-5">
                            {translate('salary.retention.debt.total', language)}:
                          </span>
                          <span className="fw-bold fs-4 text-warning">
                            {formatCurrency(retentionDebt)}
                          </span>
                        </div>
                        <small className="text-muted d-block mt-2">
                          {language === 'fr' 
                            ? 'Montant total que vous payez pour les joueurs échangés avec rétention de salaire'
                            : 'Total amount you pay for players traded with salary retention'}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          </div>
        )}

        {/* Alerte d'alignement / masse salariale dépassée */}
        {leagueSettings && alignmentIssues.length > 0 && (
          <div className="alert alert-danger mb-4" role="alert">
            <div className="d-flex">
              <div className="me-3">
                <strong>Alignement incorrect</strong>
              </div>
              <div>
                <p className="mb-1">Votre alignement présente les problèmes suivants :</p>
                <ul className="mb-1 ps-3">
                  {alignmentIssues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
                <p className="mb-0"><strong>Veuillez régler l'alignement rapidement, sinon vos points de la soirée ne seront pas comptabilisés.</strong></p>
              </div>
            </div>
          </div>
        )}

        <div className={`row g-2 mb-4 ${activeTab === 'overview' ? 'd-none' : ''}`}>
          <div className="col-6 col-md-3">
            <button
              className={`btn w-100 ${activeSection === 'MAIN' && activeTab !== 'overview' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setActiveSection('MAIN');
                if (activeTab === 'scratches') {
                  setActiveTab('players');
                } else if (activeTab === 'overview') {
                  setActiveTab('players');
                }
              }}
            >
              <span className="d-none d-sm-inline">{translate('main.club', language)}</span>
              <span className="d-sm-none">Principal</span>
            </button>
          </div>
          <div className="col-6 col-md-3">
            <button
              className={`btn w-100 ${activeSection === 'RESERVES' && activeTab !== 'overview' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setActiveSection('RESERVES');
                if (activeTab === 'scratches') {
                  setActiveTab('players');
                } else if (activeTab === 'overview') {
                  setActiveTab('players');
                }
              }}
            >
              <span className="d-none d-sm-inline">{translate('reserves', language)}</span>
              <span className="d-sm-none">Réserves</span>
            </button>
          </div>
          <div className="col-6 col-md-3">
            <button
              className={`btn w-100 ${activeSection === 'INJURED' && activeTab !== 'overview' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setActiveSection('INJURED');
                if (activeTab === 'scratches') {
                  setActiveTab('players');
                } else if (activeTab === 'overview') {
                  setActiveTab('players');
                }
              }}
            >
              <Bandage size={16} className="me-1 d-none d-sm-inline" />
              <span className="d-none d-sm-inline">Liste des blessés</span>
              <span className="d-sm-none">Blessés</span>
            </button>
          </div>
          <div className="col-6 col-md-3">
            <button
              className={`btn w-100 ${activeTab === 'overview' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setActiveTab('overview');
                // Pas besoin de changer activeSection car la vue d'ensemble ignore cette valeur
              }}
            >
              <Eye size={16} className="me-1 d-none d-sm-inline" />
              <span className="d-none d-sm-inline">Vue d'ensemble</span>
              <span className="d-sm-none">Vue d'ensemble</span>
            </button>
          </div>
        </div>

        {activeSection === 'INJURED' && (
          <div className="alert alert-info mb-4">
            <div className="d-flex align-items-start">
              <div className="d-flex me-3 mt-1">
                <Bandage size={18} className="text-danger me-2" />
                <Clock size={18} className="text-warning" />
              </div>
              <div>
                <p className="mb-1"><strong>Légende des icônes de blessure :</strong></p>
                <ul className="mb-0 ps-3">
                  <li><span className="text-danger"><Bandage size={16} className="me-1" /> Icône rouge</span> : le joueur est toujours blessé. Cliquez sur l'icône pour avoir des infos sur la blessure.</li>
                  <li><span className="text-warning"><Clock size={16} className="me-1" /> Icône jaune</span> : le joueur n'est plus blessé et peut être réactivé. Cliquez sur cette icône pour voir le nombre de jours maximum restants pour réactiver votre joueur sous peine de le perdre au agent libre.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab !== 'overview' && (
          <div className="row g-2 mb-4">
            <div className="col-6 col-md-3">
              <button
                className={`btn w-100 ${activeTab === 'players' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  setActiveTab('players');
                  // Si on était dans la section scratch, retourner au club principal
                  if (activeSection === 'SCRATCH') {
                    setActiveSection('MAIN');
                  }
                }}
              >
                <Users size={16} className="me-1 d-none d-sm-inline" />
                <span className="d-none d-sm-inline">{translate('players', language)}</span>
                <span className="d-sm-none">Joueurs</span>
              </button>
            </div>
            <div className="col-6 col-md-3">
              <button
                className={`btn w-100 ${activeTab === 'goalies' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  setActiveTab('goalies');
                  // Si on était dans la section scratch, retourner au club principal
                  if (activeSection === 'SCRATCH') {
                    setActiveSection('MAIN');
                  }
                }}
              >
                <Users size={16} className="me-1 d-none d-sm-inline" />
                <span className="d-none d-sm-inline">{translate('goalies', language)}</span>
                <span className="d-sm-none">Gardiens</span>
              </button>
            </div>
            {(activeSection === 'MAIN' || activeSection === 'SCRATCH') && (
              <>
                <div className="col-6 col-md-3">
                  <button
                    className={`btn w-100 ${activeTab === 'scratches' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => {
                      setActiveTab('scratches');
                      setActiveSection('SCRATCH');
                    }}
                  >
                    <Users size={16} className="me-1 d-none d-sm-inline" />
                    <span className="d-none d-sm-inline">Scratch Players</span>
                    <span className="d-sm-none">S. Players</span>
                  </button>
                </div>
                <div className="col-6 col-md-3">
                  <button
                    className={`btn w-100 ${activeTab === 'scratchGoalies' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => {
                      setActiveTab('scratchGoalies');
                      setActiveSection('SCRATCH');
                    }}
                  >
                    <Users size={16} className="me-1 d-none d-sm-inline" />
                    <span className="d-none d-sm-inline">Scratch Goalies</span>
                    <span className="d-sm-none">S. Goalies</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Stats NHL */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>
            {language === 'fr' ? 'Statistiques :' : 'Statistics:'}
          </span>
          <div className="btn-group btn-group-sm" role="group" aria-label={language === 'fr' ? 'Mode statistiques' : 'Stats mode'}>
            <button
              type="button"
              className={`btn ${statsMode === 'nhl' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setStatsMode('nhl')}
            >
              {language === 'fr' ? 'Stats NHL' : 'NHL Stats'}
            </button>
          </div>
          {statsMode === 'pool' && (
            <small className="text-success fst-italic">
              {language === 'fr'
                ? 'Points accumulés dans votre club depuis le début de la saison'
                : 'Points accumulated in your club since the start of the season'}
            </small>
          )}
        </div>

        {/* Barre compacte (vue d'ensemble): titre + légende + swap */}
        {activeTab === 'overview' ? (
          <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-nowrap overflow-auto">
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              {sortedMainForwards.length > 0 && (
                <div className="d-flex align-items-center">
                  <Users size={20} className={`me-2 ${theme === 'dark' ? 'text-white' : 'text-primary'} d-md-none`} />
                  <Users size={20} className="me-2 text-primary d-none d-md-block" />
                  <span className="fw-semibold d-none d-sm-inline">
                    {language === 'fr' ? 'Club Principal - Attaquants' : 'Main Club - Forwards'}
                  </span>
                  <span className="badge bg-primary ms-2">{sortedMainForwards.length}</span>
                </div>
              )}
              <ActionLegend language={language} isBpmSession={isBpmSession} />
            </div>
            {!limitedTeamActions && (
              <button
                type="button"
                className={`btn btn-sm ${multiSelectMode ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={toggleMultiSelectMode}
                title={language === 'fr'
                  ? 'Mode de sélection multiple (permutation de 4 joueurs)'
                  : 'Multi-select mode (4-player swap)'}
              >
                <Users size={16} className="me-1" />
                <span className="d-none d-sm-inline">
                  {language === 'fr' ? 'Swap 4 Joueurs' : '4 Players Swap'}
                </span>
                {multiSelectMode && (
                  <span className="badge bg-dark ms-1">
                    {selectedMainPlayers.length + selectedReservePlayers.length}/4
                  </span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div></div>
            <ActionLegend language={language} isBpmSession={isBpmSession} />
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="overview-sections">

            {/* Message d'aide pour les swaps */}
            {(selectedPlayer || selectedGoalie) && !multiSelectMode && (
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <RefreshCw size={16} className="me-2" />
                  <div>
                    <strong>Échange en cours:</strong> Vous avez sélectionné{' '}
                    {selectedPlayer 
                      ? `un joueur ${selectedPlayerSection === 'MAIN' ? 'du club principal' : selectedPlayerSection === 'RESERVES' ? 'des réserves' : selectedPlayerSection === 'SCRATCH' ? 'scratch' : 'blessé'}.`
                      : `un gardien ${selectedGoalieSection === 'MAIN' ? 'du club principal' : selectedGoalieSection === 'RESERVES' ? 'des réserves' : selectedGoalieSection === 'SCRATCH' ? 'scratch' : 'blessé'}.`
                    }
                    {' '}Cliquez sur un autre {selectedPlayer ? 'joueur' : 'gardien'} d'une section différente pour effectuer l'échange.
                  </div>
                </div>
              </div>
            )}

            {/* Message d'aide pour le mode multi-sélection en vue d'ensemble */}
            {!limitedTeamActions && multiSelectMode && (
              <div className="alert alert-warning mb-4">
                <div className="d-flex align-items-center">
                  <Users size={18} className="me-2" />
                  <div>
                    <strong>
                      {language === 'fr' 
                        ? 'Mode Permutation 4 Joueurs' 
                        : '4-Player Swap Mode'}
                    </strong>
                    <div className="mt-1">
                      {language === 'fr' 
                        ? `Sélectionnez 2 joueurs du club principal et 2 joueurs des réserves. Actuellement sélectionnés : ${selectedMainPlayers.length}/2 (principal), ${selectedReservePlayers.length}/2 (réserves)` 
                        : `Select 2 main club players and 2 reserve players. Currently selected: ${selectedMainPlayers.length}/2 (main), ${selectedReservePlayers.length}/2 (reserves)`}
                    </div>
                    {selectedMainPlayers.length > 0 && (
                      <div className="mt-1">
                        <small><strong>{language === 'fr' ? 'Club Principal:' : 'Main Club:'}</strong> {selectedMainPlayers.map(p => p.name).join(', ')}</small>
                      </div>
                    )}
                    {selectedReservePlayers.length > 0 && (
                      <div className="mt-1">
                        <small><strong>{language === 'fr' ? 'Réserves:' : 'Reserves:'}</strong> {selectedReservePlayers.map(p => p.name).join(', ')}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bouton Compléter la permutation à 4 joueurs */}
            {!limitedTeamActions && multiSelectMode && selectedMainPlayers.length === 2 && selectedReservePlayers.length === 2 && (
              <div ref={fourPlayerSwapButtonOverviewRef} className="text-center mb-4">
                <button
                  className="btn btn-success btn-lg"
                  onClick={handleConfirmFourPlayerSwap}
                  disabled={loading}
                >
                  <Users size={20} className="me-2" />
                  {language === 'fr' ? 'Compléter la Permutation (4 Joueurs)' : 'Complete 4-Player Swap'}
                  {loading && <span className="spinner-border spinner-border-sm ms-2" />}
                </button>
              </div>
            )}
            
            {/* Club Principal Section */}
            <div className="section-group mb-4">
              {/* Club Principal - Attaquants */}
              {sortedMainForwards.length > 0 && (
                <div className="mb-4">
                  {/* Titre déplacé dans la barre compacte (légende / swap) */}
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedMainForwards}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='MAIN'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                      watchlistPlayerIds={watchlistPlayerIds}
                      onAddToWatchlist={handleAddToWatchlist}
                      onRemoveFromWatchlist={handleRemoveFromWatchlist}
                      watchlistActionInProgress={watchlistActionInProgress}
                      statsMode={statsMode}
                      poolStats={poolStats}
                    />
                  </div>
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedMainForwards.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='MAIN'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Club Principal - Défenseurs */}
              {sortedMainDefense.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className={`me-2 ${theme === 'dark' ? 'text-white' : 'text-primary'} d-md-none`} />
                    <Users size={20} className="me-2 text-primary d-none d-md-block" />
                    {language === 'fr' ? 'Club Principal - Défenseurs' : 'Main Club - Defensemen'}
                    <span className="badge bg-primary ms-2">{sortedMainDefense.length}</span>
                  </h5>
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedMainDefense}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='MAIN'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedMainDefense.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='MAIN'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Club Principal - Gardiens */}
              {sortedMainGoalies.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className={`me-2 ${theme === 'dark' ? 'text-white' : 'text-primary'} d-md-none`} />
                    <Users size={20} className="me-2 text-primary d-none d-md-block" />
                    {language === 'fr' ? 'Club Principal - Gardiens' : 'Main Club - Goalies'}
                    <span className="badge bg-primary ms-2">{sortedMainGoalies.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <GoaliesTable
                      goalies={sortedMainGoalies}
                      sortConfig={goalieSortConfig}
                      onSort={handleGoalieSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedGoalie={selectedGoalie}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='MAIN'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveGoalieToScratch}
                      onRemoveFromScratch={handleRemoveGoalieFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedMainGoalies.map((goalie) => (
                        <div key={goalie.playerid} className="col-12">
                          <MobilePlayerActions
                            player={goalie}
                            isGoalie={true}
                            selectedGoalie={selectedGoalie}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='MAIN'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveGoalieToScratch}
                            onRemoveFromScratch={handleRemoveGoalieFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Scratch Players - Attaquants */}
              {sortedScratchForwardsOverview.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className="me-2 text-warning" />
                    {language === 'fr' ? 'Scratch - Attaquants' : 'Scratch - Forwards'}
                    <span className="badge bg-warning ms-2">{sortedScratchForwardsOverview.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedScratchForwardsOverview}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='SCRATCH'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedScratchForwardsOverview.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='SCRATCH'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Scratch Players - Défenseurs */}
              {sortedScratchDefenseOverview.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className="me-2 text-warning" />
                    {language === 'fr' ? 'Scratch - Défenseurs' : 'Scratch - Defensemen'}
                    <span className="badge bg-warning ms-2">{sortedScratchDefenseOverview.length}</span>
                  </h5>
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedScratchDefenseOverview}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='SCRATCH'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedScratchDefenseOverview.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='SCRATCH'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Scratch Goalies */}
              {sortedScratchGoaliesOverview.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className="me-2 text-warning" />
                    {language === 'fr' ? 'Scratch Gardiens' : 'Scratch Goalies'}
                    <span className="badge bg-warning ms-2">{sortedScratchGoaliesOverview.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <GoaliesTable
                      goalies={sortedScratchGoaliesOverview}
                      sortConfig={goalieSortConfig}
                      onSort={handleGoalieSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedGoalie={selectedGoalie}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='SCRATCH'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveGoalieToScratch}
                      onRemoveFromScratch={handleRemoveGoalieFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedScratchGoaliesOverview.map((goalie) => (
                        <div key={goalie.playerid} className="col-12">
                          <MobilePlayerActions
                            player={goalie}
                            isGoalie={true}
                            selectedGoalie={selectedGoalie}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='SCRATCH'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveGoalieToScratch}
                            onRemoveFromScratch={handleRemoveGoalieFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Réserves Section */}
            <div className="section-group mb-4">
              {/* Réserves - Attaquants */}
              {sortedReserveForwards.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className={`me-2 ${theme === 'dark' ? 'text-white' : 'text-info'} d-md-none`} />
                    <Users size={20} className="me-2 text-info d-none d-md-block" />
                    {language === 'fr' ? 'Réserves - Attaquants' : 'Reserves - Forwards'}
                    <span className="badge bg-info ms-2">{sortedReserveForwards.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedReserveForwards}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='RESERVES'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedReserveForwards.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='RESERVES'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Réserves - Défenseurs */}
              {sortedReserveDefense.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className={`me-2 ${theme === 'dark' ? 'text-white' : 'text-info'} d-md-none`} />
                    <Users size={20} className="me-2 text-info d-none d-md-block" />
                    {language === 'fr' ? 'Réserves - Défenseurs' : 'Reserves - Defensemen'}
                    <span className="badge bg-info ms-2">{sortedReserveDefense.length}</span>
                  </h5>
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedReserveDefense}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='RESERVES'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedReserveDefense.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='RESERVES'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Réserves - Gardiens */}
              {sortedReserveGoalies.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Users size={20} className={`me-2 ${theme === 'dark' ? 'text-white' : 'text-info'} d-md-none`} />
                    <Users size={20} className="me-2 text-info d-none d-md-block" />
                    {language === 'fr' ? 'Réserves - Gardiens' : 'Reserves - Goalies'}
                    <span className="badge bg-info ms-2">{sortedReserveGoalies.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <GoaliesTable
                      goalies={sortedReserveGoalies}
                      sortConfig={goalieSortConfig}
                      onSort={handleGoalieSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedGoalie={selectedGoalie}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='RESERVES'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveGoalieToScratch}
                      onRemoveFromScratch={handleRemoveGoalieFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedReserveGoalies.map((goalie) => (
                        <div key={goalie.playerid} className="col-12">
                          <MobilePlayerActions
                            player={goalie}
                            isGoalie={true}
                            selectedGoalie={selectedGoalie}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='RESERVES'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveGoalieToScratch}
                            onRemoveFromScratch={handleRemoveGoalieFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Liste des blessés Section - En dernier */}
            <div className="section-group">
              {/* Liste des blessés - Attaquants */}
              {sortedInjuredForwards.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Bandage size={20} className="me-2 text-danger" />
                    {language === 'fr' ? 'Liste des blessés - Attaquants' : 'Injured List - Forwards'}
                    <span className="badge bg-danger ms-2">{sortedInjuredForwards.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedInjuredForwards}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='INJURED'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedInjuredForwards.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='INJURED'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Liste des blessés - Défenseurs */}
              {sortedInjuredDefense.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Bandage size={20} className="me-2 text-danger" />
                    {language === 'fr' ? 'Liste des blessés - Défenseurs' : 'Injured List - Defensemen'}
                    <span className="badge bg-danger ms-2">{sortedInjuredDefense.length}</span>
                  </h5>
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <MyTeamsTable
                      players={sortedInjuredDefense}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedPlayer={selectedPlayer}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='INJURED'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveToScratch}
                      onRemoveFromScratch={handleRemoveFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedInjuredDefense.map((player) => (
                        <div key={player.playerid} className="col-12">
                          <MobilePlayerActions
                            player={player}
                            selectedPlayer={selectedPlayer}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='INJURED'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveToScratch}
                            onRemoveFromScratch={handleRemoveFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Liste des blessés - Gardiens */}
              {sortedInjuredGoalies.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <Bandage size={20} className="me-2 text-danger" />
                    {language === 'fr' ? 'Liste des blessés - Gardiens' : 'Injured List - Goalies'}
                    <span className="badge bg-danger ms-2">{sortedInjuredGoalies.length}</span>
                  </h5>
                  
                  {/* Vue Desktop */}
                  <div className="d-none d-md-block">
                    <GoaliesTable
                      goalies={sortedInjuredGoalies}
                      sortConfig={goalieSortConfig}
                      onSort={handleGoalieSort}
                      onRelease={handleRelease}
                      onToggleReserve={handleToggleReserve}
                      onSwapSelect={handleOverviewSwapSelect}
                      selectedGoalie={selectedGoalie}
                      language={language}
                      injuredPlayers={injuryDescriptions}
                      activeSection='INJURED'
                      onSendToInjuredList={handleSendToInjuredList}
                      onReactivateFromInjured={handleReactivateFromInjuredList}
                      onMoveToScratch={handleMoveGoalieToScratch}
                      onRemoveFromScratch={handleRemoveGoalieFromScratch}
                      onToggleTradingBloc={handleToggleTradingBloc}
                      onToggleIntouchable={handleToggleIntouchable}
                      onViewDetails={handleViewPlayerDetails}
                      hideActions={false}
                    watchlistPlayerIds={watchlistPlayerIds}
                    onAddToWatchlist={handleAddToWatchlist}
                    onRemoveFromWatchlist={handleRemoveFromWatchlist}
                    watchlistActionInProgress={watchlistActionInProgress}
                    statsMode={statsMode}
                    poolStats={poolStats}
                    />
                  </div>
                  
                  {/* Vue Mobile */}
                  <div className="d-md-none">
                    <div className="row g-2">
                      {sortedInjuredGoalies.map((goalie) => (
                        <div key={goalie.playerid} className="col-12">
                          <MobilePlayerActions
                            player={goalie}
                            isGoalie={true}
                            selectedGoalie={selectedGoalie}
                            language={language}
                            statsMode={statsMode}
                            poolStats={poolStats}
                            activeSection='INJURED'
                            injuredPlayers={injuryDescriptions}
                            onRelease={handleRelease}
                            onToggleReserve={handleToggleReserve}
                            onSwapSelect={handleOverviewSwapSelect}
                            onSendToInjuredList={handleSendToInjuredList}
                            onReactivateFromInjured={handleReactivateFromInjuredList}
                            onMoveToScratch={handleMoveGoalieToScratch}
                            onRemoveFromScratch={handleRemoveGoalieFromScratch}
                            onToggleTradingBloc={handleToggleTradingBloc}
                            onToggleIntouchable={handleToggleIntouchable}
                            onViewDetails={handleViewPlayerDetails}
                            multiSelectMode={multiSelectMode}
                            isPlayerSelectedInMultiMode={(p) => selectedMainPlayers.some(sp => sp.id === p.playerid) || selectedReservePlayers.some(sp => sp.id === p.playerid)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message si l'équipe est vide */}
            {(mainPlayers.length + reservePlayers.length + injuredPlayers.length + scratchPlayers.length +
              mainGoalies.length + reserveGoalies.length + injuredGoalies.length + scratchGoalies.length === 0) && (
              <div className="text-center p-5">
                <p>{language === 'fr' ? 'Aucun joueur trouvé avec les filtres actuels.' : 'No players found with current filters.'}</p>
              </div>
            )}

            {/* Choix au repêchage Section */}
            {draftPicks.length > 0 && (
              <div className="mb-4">
                <h5 className="mb-3 d-flex align-items-center">
                  <span className="me-2">📋</span>
                  {language === 'fr' ? 'Choix au repêchage' : 'Draft Picks'}
                  <span className="badge bg-warning text-dark ms-2">{draftPicks.length}</span>
                </h5>
                
                <div className="row g-3">
                  {draftPicks.map((pick) => (
                    <div key={pick.id} className="col-md-6 col-lg-4">
                      <div className="card border-warning h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3" 
                                 style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                              <span className="fw-bold text-dark">📋</span>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold text-warning">{pick.description}</h6>
                              <small className="text-muted">
                                {language === 'fr' ? 'Choix de repêchage' : 'Draft Pick'}
                              </small>
                            </div>
                          </div>
                          
                          {pick.originalUserName && (
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Propriétaire original:</strong> {pick.originalUserName}
                              </small>
                            </div>
                          )}
                          
                          <div className="mt-2">
                            <span className="badge bg-warning text-dark">
                              Ronde {pick.roundNumber}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'players' && (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <PositionFilterMyTeams
                  selectedPosition={selectedPosition}
                  onPositionChange={setSelectedPosition}
                  language={language}
                />
                
                <div className="d-flex align-items-center gap-3">
                  {/* Bouton pour le mode de sélection multiple */}
                  {!limitedTeamActions && (
                    <button
                      type="button"
                      className={`btn btn-sm ${multiSelectMode ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={toggleMultiSelectMode}
                      title={language === 'fr' 
                        ? 'Mode de sélection multiple (permutation de 4 joueurs)' 
                        : 'Multi-select mode (4-player swap)'}
                    >
                      <Users size={16} className="me-1" />
                      {language === 'fr' ? '4 Joueurs' : '4 Players'}
                      {multiSelectMode && (
                        <span className="badge bg-dark ms-1">
                          {selectedMainPlayers.length + selectedReservePlayers.length}/4
                        </span>
                      )}
                    </button>
                  )}
                  
                  <div className="d-flex align-items-center">
                    <label htmlFor="itemsPerPage" className="me-2 mb-0">
                      {language === 'en' ? 'Players per page:' : 'Joueurs par page:'}
                    </label>
                    <select
                      id="itemsPerPage"
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile-only section header for aesthetics, logic unchanged */}
            <div className="d-md-none mb-2">
              <div className="mobile-section-card">
                <div className="msc-title d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Users size={18} className={theme === 'dark' ? 'text-white' : 'text-primary'} />
                    <span className="msc-title-text">
                      {activeSection === 'MAIN' ? (language === 'fr' ? 'Club Principal - Joueurs' : 'Main Club - Players')
                        : activeSection === 'RESERVES' ? (language === 'fr' ? 'Réserves - Joueurs' : 'Reserves - Players')
                        : (language === 'fr' ? 'Liste des blessés - Joueurs' : 'Injured List - Players')}
                    </span>
                  </div>
                  <span className="msc-badge">{sortedPlayers.length}</span>
                </div>
              </div>
            </div>

            {/* Message d'aide pour les permutations */}
            {selectedPlayer && selectedPlayerSection !== 'INJURED' && !showSwapModal && !multiSelectMode && (
              <div ref={playerSwapHelpRef} className="alert alert-info mb-3">
                <div className="d-flex align-items-center">
                  <RefreshCw size={18} className="me-2" />
                  <span>
                    {selectedPlayerSection === 'MAIN' 
                      ? (language === 'fr' 
                          ? 'Choisissez un joueur du club école (réserve) ou un "scratch player" pour compléter la permutation.' 
                          : 'Choose a player from the reserve team to complete the swap.')
                      : (language === 'fr' 
                          ? 'Choisissez un joueur du club principal pour compléter la permutation.' 
                          : 'Choose a player from the main club to complete the swap.')
                    }
                  </span>
                </div>
              </div>
            )}

            {/* Message d'aide pour le mode multi-sélection */}
            {!limitedTeamActions && multiSelectMode && (
              <div className="alert alert-warning mb-3">
                <div className="d-flex align-items-center">
                  <Users size={18} className="me-2" />
                  <div>
                    <strong>
                      {language === 'fr' 
                        ? 'Mode Permutation 4 Joueurs' 
                        : '4-Player Swap Mode'}
                    </strong>
                    <div className="mt-1">
                      {language === 'fr' 
                        ? `Sélectionnez 2 joueurs du club principal et 2 joueurs des réserves. Actuellement sélectionnés : ${selectedMainPlayers.length}/2 (principal), ${selectedReservePlayers.length}/2 (réserves)` 
                        : `Select 2 main club players and 2 reserve players. Currently selected: ${selectedMainPlayers.length}/2 (main), ${selectedReservePlayers.length}/2 (reserves)`}
                    </div>
                    {selectedMainPlayers.length > 0 && (
                      <div className="mt-1">
                        <small><strong>{language === 'fr' ? 'Club Principal:' : 'Main Club:'}</strong> {selectedMainPlayers.map(p => p.name).join(', ')}</small>
                      </div>
                    )}
                    {selectedReservePlayers.length > 0 && (
                      <div className="mt-1">
                        <small><strong>{language === 'fr' ? 'Réserves:' : 'Reserves:'}</strong> {selectedReservePlayers.map(p => p.name).join(', ')}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bouton Compléter la permutation à 4 joueurs pour l'onglet Players */}
            {!limitedTeamActions && multiSelectMode && selectedMainPlayers.length === 2 && selectedReservePlayers.length === 2 && (
              <div ref={fourPlayerSwapButtonPlayersRef} className="text-center mb-3">
                <button
                  className="btn btn-success btn-lg"
                  onClick={handleConfirmFourPlayerSwap}
                  disabled={loading}
                >
                  <Users size={20} className="me-2" />
                  {language === 'fr' ? 'Compléter la Permutation (4 Joueurs)' : 'Complete 4-Player Swap'}
                  {loading && <span className="spinner-border spinner-border-sm ms-2" />}
                </button>
              </div>
            )}

            {paginatedPlayers.length === 0 ? (
              <div className="text-center p-5">
                <p>
                  {translate('no.players.available', language, {
                    section: activeSection === 'MAIN' 
                      ? 'Club Principal' 
                      : activeSection === 'RESERVES'
                        ? 'Réserves'
                        : 'Liste des blessés'
                  })}
                </p>
              </div>
            ) : (
              <MyTeamsTable
                players={paginatedPlayers}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRelease={handleRelease}
                onToggleReserve={handleToggleReserve}
                onSwapSelect={multiSelectMode ? handleMultiSelect : handleSwapSelect}
                selectedPlayer={selectedPlayer}
                multiSelectMode={multiSelectMode}
                isPlayerSelectedInMultiMode={isPlayerSelectedInMultiMode}
                language={language}
                injuredPlayers={injuryDescriptions}
                activeSection={activeSection}
                onSendToInjuredList={handleSendToInjuredList}
                onReactivateFromInjured={handleReactivateFromInjuredList}
                onMoveToScratch={handleMoveToScratch}
                onRemoveFromScratch={handleRemoveFromScratch}
                onToggleTradingBloc={handleToggleTradingBloc}
                onToggleIntouchable={handleToggleIntouchable}
                onViewDetails={handleViewPlayerDetails}
                statsMode={statsMode}
                poolStats={poolStats}
              />
            )}
          </>
        )}

        {activeTab === 'goalies' && (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-end align-items-center">
                {/* Bouton pour le mode de sélection multiple pour les gardiens */}
                {!limitedTeamActions && (
                  <button
                    type="button"
                    className={`btn btn-sm ${multiSelectMode ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={toggleMultiSelectMode}
                    title={language === 'fr' 
                      ? 'Mode de sélection multiple (permutation de 4 gardiens)' 
                      : 'Multi-select mode (4-goalie swap)'}
                  >
                    <Users size={16} className="me-1" />
                    {language === 'fr' ? '4 Gardiens' : '4 Goalies'}
                    {multiSelectMode && (
                      <span className="badge bg-dark ms-1">
                        {selectedMainPlayers.length + selectedReservePlayers.length}/4
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Message d'aide pour les permutations des gardiens */}
            {selectedGoalie && selectedGoalieSection !== 'INJURED' && !showSwapModal && !multiSelectMode && (
              <div ref={goalieSwapHelpRef} className="alert alert-info mb-3">
                <div className="d-flex align-items-center">
                  <RefreshCw size={18} className="me-2" />
                  <span>
                    {selectedGoalieSection === 'MAIN' 
                      ? (language === 'fr' 
                          ? 'Choisissez un gardien du club école pour compléter la permutation.' 
                          : 'Choose a goalie from the reserve team to complete the swap.')
                      : (language === 'fr' 
                          ? 'Choisissez un gardien du club principal pour compléter la permutation.' 
                          : 'Choose a goalie from the main club to complete the swap.')
                    }
                  </span>
                </div>
              </div>
            )}

            {/* Message d'aide pour le mode multi-sélection des gardiens */}
            {!limitedTeamActions && multiSelectMode && (
              <div className="alert alert-warning mb-3">
                <div className="d-flex align-items-center">
                  <Users size={18} className="me-2" />
                  <div>
                    <strong>
                      {language === 'fr' 
                        ? 'Mode Permutation 4 Gardiens' 
                        : '4-Goalie Swap Mode'}
                    </strong>
                    <div className="mt-1">
                      {language === 'fr' 
                        ? `Sélectionnez 2 gardiens du club principal et 2 gardiens des réserves. Actuellement sélectionnés : ${selectedMainPlayers.length}/2 (principal), ${selectedReservePlayers.length}/2 (réserves)` 
                        : `Select 2 main club goalies and 2 reserve goalies. Currently selected: ${selectedMainPlayers.length}/2 (main), ${selectedReservePlayers.length}/2 (reserves)`}
                    </div>
                    {selectedMainPlayers.length > 0 && (
                      <div className="mt-1">
                        <small><strong>{language === 'fr' ? 'Club Principal:' : 'Main Club:'}</strong> {selectedMainPlayers.map(p => p.name).join(', ')}</small>
                      </div>
                    )}
                    {selectedReservePlayers.length > 0 && (
                      <div className="mt-1">
                        <small><strong>{language === 'fr' ? 'Réserves:' : 'Reserves:'}</strong> {selectedReservePlayers.map(p => p.name).join(', ')}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bouton Compléter la permutation à 4 gardiens pour l'onglet Goalies */}
            {!limitedTeamActions && multiSelectMode && selectedMainPlayers.length === 2 && selectedReservePlayers.length === 2 && (
              <div ref={fourPlayerSwapButtonGoaliesRef} className="text-center mb-3">
                <button
                  className="btn btn-success btn-lg"
                  onClick={handleConfirmFourPlayerSwap}
                  disabled={loading}
                >
                  <Users size={20} className="me-2" />
                  {language === 'fr' ? 'Compléter la Permutation (4 Gardiens)' : 'Complete 4-Goalie Swap'}
                  {loading && <span className="spinner-border spinner-border-sm ms-2" />}
                </button>
              </div>
            )}

            {/* Mobile-only section header for aesthetics, logic unchanged */}
            <div className="d-md-none mb-2">
              <div className="mobile-section-card">
                <div className="msc-title d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Users size={18} className={theme === 'dark' ? 'text-white' : 'text-primary'} />
                    <span className="msc-title-text">
                      {activeSection === 'MAIN' ? (language === 'fr' ? 'Club Principal - Gardiens' : 'Main Club - Goalies')
                        : activeSection === 'RESERVES' ? (language === 'fr' ? 'Réserves - Gardiens' : 'Reserves - Goalies')
                        : (language === 'fr' ? 'Liste des blessés - Gardiens' : 'Injured List - Goalies')}
                    </span>
                  </div>
                  <span className="msc-badge">{sortedGoalies.length}</span>
                </div>
              </div>
            </div>

            {paginatedGoalies.length === 0 ? (
              <div className="text-center p-5">
                <p>
                  {translate('no.goalies.available', language, {
                    section: activeSection === 'MAIN' 
                      ? 'Club Principal' 
                      : activeSection === 'RESERVES'
                        ? 'Réserves'
                        : 'Liste des blessés'
                  })}
                </p>
              </div>
            ) : (
              <GoaliesTable
                goalies={paginatedGoalies}
                sortConfig={goalieSortConfig}
                onSort={handleGoalieSort}
                onRelease={handleRelease}
                onToggleReserve={handleToggleReserve}
                onSwapSelect={multiSelectMode ? handleMultiSelect : handleSwapSelect}
                selectedGoalie={selectedGoalie}
                multiSelectMode={multiSelectMode}
                isPlayerSelectedInMultiMode={isPlayerSelectedInMultiMode}
                language={language}
                injuredPlayers={injuryDescriptions}
                activeSection={activeSection}
                onSendToInjuredList={handleSendToInjuredList}
                onReactivateFromInjured={handleReactivateFromInjuredList}
                onMoveToScratch={handleMoveGoalieToScratch}
                onRemoveFromScratch={handleRemoveGoalieFromScratch}
                onToggleTradingBloc={handleToggleTradingBloc}
                onToggleIntouchable={handleToggleIntouchable}
                onViewDetails={handleViewPlayerDetails}
                statsMode={statsMode}
                poolStats={poolStats}
              />
            )}
          </>
        )}

        {activeTab === 'scratches' && (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <PositionFilterMyTeams
                  selectedPosition={selectedPosition}
                  onPositionChange={setSelectedPosition}
                  language={language}
                />
                
                <div className="d-flex align-items-center">
                  <label htmlFor="itemsPerPage" className="me-2 mb-0">
                    {language === 'en' ? 'Players per page:' : 'Joueurs par page:'}
                  </label>
                  <select
                    id="itemsPerPage"
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="alert alert-info mb-3">
              <h6 className="mb-2">🏒 Scratch Players</h6>
              <p className="mb-0">
                Les Scratch Players sont des joueurs qui comptent sur la masse salariale principale 
                mais dont les points ne comptent pas dans le classement. Appuyer sur le bouton vert pour retourner le joueur dans le club principal.
              </p>
            </div>

            {/* Mobile-only section header for aesthetics, logic unchanged */}
            <div className="d-md-none mb-2">
              <div className="mobile-section-card">
                <div className="msc-title d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Users size={18} className="text-warning" />
                    <span className="msc-title-text">Scratch Players</span>
                  </div>
                  <span className="msc-badge">{sortedScratchPlayers.length}</span>
                </div>
              </div>
            </div>

            {paginatedScratchPlayers.length === 0 ? (
              <div className="text-center p-5">
                <p>
                  {language === 'fr' 
                    ? 'Aucun scratch player disponible.'
                    : 'No scratch players available.'}
                </p>
              </div>
            ) : (
              <MyTeamsTable
                players={paginatedScratchPlayers}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRelease={handleRelease}
                onToggleReserve={handleToggleReserve}
                onSwapSelect={handleSwapSelect}
                selectedPlayer={selectedPlayer}
                language={language}
                injuredPlayers={injuryDescriptions}
                activeSection='SCRATCH'
                onSendToInjuredList={handleSendToInjuredList}
                onReactivateFromInjured={handleReactivateFromInjuredList}
                onMoveToScratch={handleMoveToScratch}
                onRemoveFromScratch={handleRemoveFromScratch}
                onViewDetails={handleViewPlayerDetails}
                statsMode={statsMode}
                poolStats={poolStats}
              />
            )}
          </>
        )}

        {activeTab === 'scratchGoalies' && (
          <>
            <div className="alert alert-info mb-3">
              <h6 className="mb-2">Scratch Goalies</h6>
              <p className="mb-0">
                Les Scratch Goalies sont des gardiens qui comptent sur la masse salariale principale 
                mais dont les points ne comptent pas dans le classement.
              </p>
            </div>

            {/* Mobile-only section header for aesthetics, logic unchanged */}
            <div className="d-md-none mb-2">
              <div className="mobile-section-card">
                <div className="msc-title d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Users size={18} className="text-warning" />
                    <span className="msc-title-text">{language === 'fr' ? 'Scratch Gardiens' : 'Scratch Goalies'}</span>
                  </div>
                  <span className="msc-badge">{sortedGoalies.length}</span>
                </div>
              </div>
            </div>

            {paginatedGoalies.length === 0 ? (
              <div className="text-center p-5">
                <p>Aucun gardien scratch trouvé.</p>
              </div>
            ) : (
              <GoaliesTable
                goalies={paginatedGoalies}
                sortConfig={goalieSortConfig}
                onSort={handleGoalieSort}
                onRelease={handleRelease}
                onToggleReserve={handleToggleReserve}
                onSwapSelect={handleSwapSelect}
                selectedGoalie={selectedGoalie}
                language={language}
                injuredPlayers={injuryDescriptions}
                activeSection='SCRATCH'
                onSendToInjuredList={handleSendToInjuredList}
                onReactivateFromInjured={handleReactivateFromInjuredList}
                onMoveToScratch={handleMoveGoalieToScratch}
                onRemoveFromScratch={handleRemoveGoalieFromScratch}
                onViewDetails={handleViewPlayerDetails}
                statsMode={statsMode}
                poolStats={poolStats}
              />
            )}
          </>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <button
              className="btn btn-outline-primary mx-1"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              {translate('previous', language)}
            </button>
            <div className="mx-3 d-flex align-items-center">
              {translate('page', language)} {currentPage} {translate('of', language)} {totalPages}
            </div>
            <button
              className="btn btn-outline-primary mx-1"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              {translate('next', language)}
            </button>
          </div>
        )}
        
        <SwapPlayerModal
          show={showSwapModal}
          mainPlayer={swapDetails?.mainName || ''}
          reservePlayer={swapDetails?.reserveName || ''}
          onSwap={handleConfirmSwap}
          onCancel={() => {
            setShowSwapModal(false);
            setSwapDetails(null);
            setSwapError(null);
            // Remettre à zéro les sélections lors de l'annulation
            setSelectedPlayer(null);
            setSelectedGoalie(null);
            setSelectedPlayerSection(null);
            setSelectedGoalieSection(null);
          }}
          isProcessing={processingSwap}
          language={language}
          swapType={swapDetails?.swapType || 'normal'}
          errorMessage={swapError}
        />

        <SwapFourPlayersModal
          show={showFourPlayerSwapModal}
          mainPlayers={selectedMainPlayers}
          reservePlayers={selectedReservePlayers}
          onSwap={handleConfirmFourPlayerSwap}
          onCancel={() => {
            setShowFourPlayerSwapModal(false);
            setSelectedMainPlayers([]);
            setSelectedReservePlayers([]);
            setMultiSelectMode(false);
          }}
          isProcessing={processingSwap}
          language={language}
        />

        <PromotePlayerModal
          show={showPromoteModal}
          playerName={promotePlayerInfo?.name || ''}
          onPromoteToMain={handlePromoteToMainClub}
          onPromoteToScratch={handlePromoteToScratch}
          onClose={handleClosePromoteModal}
          isProcessing={processingPromotion}
          language={language}
          allowScratch={leagueSettings ? (leagueSettings.number_scratch !== 0) : true}
        />
        <DemoteConfirmModal
          show={showDemoteModal}
          playerName={demotePlayerInfo?.name || ''}
          onConfirm={handleConfirmDemotion}
          onCancel={() => { setShowDemoteModal(false); setDemotePlayerInfo(null); }}
          isProcessing={processingAction}
          language={language}
        />

        <ReactivateInjuredPlayerModal
          show={showReactivateModal}
          playerName={reactivatePlayerInfo?.name || ''}
          isGoalie={reactivatePlayerInfo?.isGoalie || false}
          mainPlayers={mainPlayers}
          reservePlayers={reservePlayers}
          mainGoalies={mainGoalies}
          reserveGoalies={reserveGoalies}
          hasReserveSpots={Boolean(leagueSettings && Number(leagueSettings.number_reserve) > 0)}
          onReactivateDirectly={handleDirectReactivation}
          onReactivateWithSwap={handleReactivationWithSwap}
          allowReplacePlayer={isStandardFlexible}
          injuredPositionCode={reactivatePlayerInfo?.raw?.positioncode}
          reserveFull={Boolean(leagueSettings && Number(leagueSettings.number_reserve) > 0 && reserveTotalCount >= Number(leagueSettings.number_reserve))}
          onReactivateReplacing={handleReactivationReplacingPlayer}
          onClose={() => { setShowReactivateModal(false); setReactivatePlayerInfo(null); }}
          isProcessing={processingAction}
          language={language}
          rosterLimitError={(() => {
            if (!reactivatePlayerInfo || !leagueSettings) return null;
            if (reactivatePlayerInfo.isGoalie) {
              const max = leagueSettings.number_goalie || 2;
              if (mainGoalies.length >= max) {
                return language === 'fr'
                  ? `La réactivation de ce joueur ferait dépasser la limite de gardiens de votre équipe (${max}). Vous devez d'abord libérer un gardien avant de pouvoir réactiver ce joueur.`
                  : `Reactivating this player would exceed your team's goalie limit (${max}). You must first release a goalie before reactivating this player.`;
              }
            } else {
              const pos = reactivatePlayerInfo.raw?.positioncode;
              if (pos === 'D') {
                const max = leagueSettings.number_def || 6;
                const currentDef = mainPlayers.filter((p: any) => p.positioncode === 'D').length;
                if (currentDef >= max) {
                  return language === 'fr'
                    ? `La réactivation de ce joueur ferait dépasser la limite de défenseurs de votre équipe (${max}). Vous devez d'abord libérer un défenseur avant de pouvoir réactiver ce joueur.`
                    : `Reactivating this player would exceed your team's defensemen limit (${max}). You must first release a defenseman before reactivating this player.`;
                }
              } else if (['C', 'L', 'R'].includes(pos)) {
                const max = leagueSettings.number_foward || 12;
                const currentFwd = mainPlayers.filter((p: any) => ['C', 'L', 'R'].includes(p.positioncode)).length;
                if (currentFwd >= max) {
                  return language === 'fr'
                    ? `La réactivation de ce joueur ferait dépasser la limite d'attaquants de votre équipe (${max}). Vous devez d'abord libérer un attaquant avant de pouvoir réactiver ce joueur.`
                    : `Reactivating this player would exceed your team's forward limit (${max}). You must first release a forward before reactivating this player.`;
                }
              }
            }
            return null;
          })()}
        />

        {pendingReleasePlayer && (
          <MyTeamsModal onClose={() => setPendingReleasePlayer(null)} busy={isReleasingPlayer}>
          <div
            className={`position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center${isEmbed ? ' my-team-release-overlay' : ''}`}
            style={{ zIndex: 9100, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)' }}
            onClick={() => !isReleasingPlayer && setPendingReleasePlayer(null)}
          >
            <div
              className={`card shadow-lg border-0${isEmbed ? ' my-team-release-modal' : ''}`}
              ref={releaseConfirmModalRef}
              data-team-modal-card
              style={{
                maxWidth: 440,
                width: '92%',
                borderRadius: 16,
                backgroundColor: theme === 'dark' ? '#0b1220' : '#fff',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="card-body p-4">
                <h6 className="fw-bold mb-1">{language === 'fr' ? 'Libérer ce joueur ?' : 'Release this player?'}</h6>
                <p className="text-muted small mb-3">
                  <strong>{pendingReleasePlayer.skaterfullname || pendingReleasePlayer.goaliefullname}</strong>
                  {pendingReleasePlayer.salary > 0 ? ` — $${(pendingReleasePlayer.salary / 1_000_000).toFixed(2)}M` : ''}
                </p>
                <div
                  className={`rounded-3 p-3 mb-3 small${isEmbed ? ' my-team-release-details' : ''}`}
                  style={{
                    background: theme === 'dark' ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,1)',
                    border: `1px solid ${theme === 'dark' ? 'rgba(148,163,184,0.2)' : '#e2e8f0'}`,
                  }}
                >
                  {language === 'fr' ? (
                    <>
                      <div className="mb-1">La <strong>place d'alignement</strong> sera libérée.</div>
                      <div className="mb-1">La <strong>masse salariale</strong> sera récupérée.</div>
                      <div>Le joueur redeviendra <strong>agent libre</strong> et pourra être signé à nouveau.</div>
                    </>
                  ) : (
                    <>
                      <div className="mb-1">The <strong>roster spot</strong> will be freed.</div>
                      <div className="mb-1">The <strong>salary cap space</strong> will be recovered.</div>
                      <div>The player will become a <strong>free agent</strong> again.</div>
                    </>
                  )}
                </div>
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setPendingReleasePlayer(null)}
                    disabled={isReleasingPlayer}
                  >
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </button>
                  <button
                    className="btn btn-danger d-flex align-items-center gap-2"
                    onClick={handleReleaseConfirmed}
                    disabled={isReleasingPlayer}
                  >
                    {isReleasingPlayer && <span className="spinner-border spinner-border-sm" />}
                    {language === 'fr' ? 'Confirmer la libération' : 'Confirm release'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </MyTeamsModal>
        )}
      </div>
    </div>
    </MyTeamsModalProvider>
  );
}

